import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  computed,
  DestroyRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, finalize, firstValueFrom, fromEvent, of, Subject, Subscription, takeUntil } from 'rxjs';
import {
  CalendarDatePipe,
  CalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarEventTitleFormatter,
  CalendarNextViewDirective,
  CalendarPreviousViewDirective,
  CalendarView,
  CalendarWeekViewBeforeRenderEvent,
  CalendarWeekViewComponent,
} from 'angular-calendar';
import { ApiService } from 'src/app/services/api.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormBuilder, FormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { EventAction, EventColor, WeekViewHourSegment } from 'calendar-utils';
import { UtilsService } from 'src/app/services/utils.service';
import { UserService } from 'src/app/services/user.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WorkingTimesCalendarDialogComponent } from './working-times-calendar-dialog/working-times-calendar-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'src/environments/environment';
import { WorkingContract } from 'src/app/models/working_contract.model';
import dayjs from 'dayjs';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { EmployeeCalendarRegisterRelatableType } from 'src/app/enums/employee-calendar-register-relatable-type.enum';
import { EmployeeCalendarRegister, EmployeeCalendarRegisterWithRelationsSerialized } from 'src/app/models/employee_calendar_register.model';
import { CustomEventTitleFormatter } from '../../../shared/modules/custom-event-title-formatter.provider';
import { Holiday } from 'src/app/models/holiday.model';
import { User } from 'src/app/models/user.model';
import { EmployeeService, PermissionsService } from 'src/app/services';
import { addDays, addMinutes, endOfWeek } from 'date-fns';
import { CalendarEventDetailDialogComponent } from './calendar-event-detail-dialog/calendar-event-detail-dialog.component';
import { CalendarUnsavedChangesDialogComponent } from './calendar-unsaved-changes-dialog/calendar-unsaved-changes-dialog.component';
import { ComponentCanDeactivate } from 'src/app/guards/pending-changes.guard';
import { RadioSelectorDialogComponent } from '../../../shared/radio-selector-dialog/radio-selector-dialog.component';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatOption } from '@angular/material/autocomplete';
import { NgxDropzoneChangeEvent, NgxDropzoneModule } from 'ngx-dropzone';
import ICAL from 'ical.js';
import { EmployeeCalendarRegisterUserSharedEvent } from 'src/app/models/employee_calendar_register_user_shared_event.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ManageRemoteWorkDialogComponent } from './manage-remote-work-dialog/manage-remote-work-dialog.component';
import { AttendeeCalendarConflictsDialogComponent } from './attendee-calendar-conflicts-dialog/attendee-calendar-conflicts-dialog.component';
import { ConfigureWorkingScheduleDialogComponent } from './configure-working-schedule-dialog/configure-working-schedule-dialog.component';
import { ConfigureWorkingScheduleDialogData } from 'src/app/models/working_contract.model';
import {
  WorkingContractWeeklySchedule,
  WorkingContractWeeklyScheduleDayPeriod,
  WorkingContractScheduleForWeekResponse,
} from 'src/app/models/working_contract_weekly_schedule.model';
import { WorkingContractWeeklyScheduleException } from 'src/app/models/working_contract_weekly_schedule_exception.model';
import { ScheduleMode } from 'src/app/enums/schedule-mode.enum';
import { MatIconButton, MatMiniFabButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { NgClass, NgStyle } from '@angular/common';
import { CalendarDateToCustomFormatPipe } from '../../../../../pipes/calendar-date-to-custom-formt.pipe';
import { DayOfWeekEnum, fromDayOfWeekIso } from 'src/app/enums/day-of-week.enum';

@Component({
  selector: 'app-working-times-calendar',
  templateUrl: './working-times-calendar.component.html',
  styleUrls: ['./working-times-calendar.component.css'],
  host: {
    '(window:beforeunload)': 'onBeforeUnload($event)',
  },
  providers: [
    {
      provide: CalendarEventTitleFormatter,
      useClass: CustomEventTitleFormatter,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIconButton,
    CalendarPreviousViewDirective,
    CalendarNextViewDirective,
    CalendarDatePipe,
    CalendarWeekViewComponent,
    MatIcon,
    MatFormField,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatTooltip,
    MatMiniFabButton,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    NgxDropzoneModule,
    NgClass,
    NgStyle,
    CalendarDateToCustomFormatPipe,
  ],
})
export class WorkingTimesCalendarComponent implements OnInit, AfterViewInit, OnDestroy, ComponentCanDeactivate {
  private readonly api = inject(ApiService);
  protected readonly utils = inject(UtilsService);
  private readonly userService = inject(UserService);
  private readonly dialog = inject(MatDialog);
  private readonly employee = inject(EmployeeService);
  public readonly permissions = inject(PermissionsService);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly fb = inject(FormBuilder);
  protected readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy_ref = inject(DestroyRef);

  me: User;
  user_selected: User;

  userOptions: User[] = [];
  active_contract: WorkingContract | null = null;

  // objecte per gestionar teletreball
  remote_work_days: Record<DayOfWeekEnum, boolean> = {
    [DayOfWeekEnum.Monday]: false,
    [DayOfWeekEnum.Tuesday]: false,
    [DayOfWeekEnum.Wednesday]: false,
    [DayOfWeekEnum.Thursday]: false,
    [DayOfWeekEnum.Friday]: false,
    [DayOfWeekEnum.Saturday]: false,
    [DayOfWeekEnum.Sunday]: false,
  };

  non_working_time_categories_that_appear_in_calendar: WorkingTimeCategory[] = [];
  avail_key_selected: string = null as any;
  availability: { [key: string]: Avail } = {};
  private deletable_ids: { id: number; apply_updates_on: 'next' | 'this' | undefined; notify: boolean }[] = [];
  absences: ('holiday' | 'vacation' | 'other' | null)[];
  private active_company_products: {
    company_product_id: number;
    company_product_name: string;
    department_category_id: number;
    slug: string;
  }[] = [];

  total_hours_assigned: number = 0;
  total_hours_created: number = 0;

  changesAvailableToSave: boolean = false;

  // Calendar Options
  segmentation_options = [
    // {
    //   name: 'Horas enteras',
    //   value: 1
    // },
    {
      name: 'Detalle',
      value: 50,
    },
    {
      name: 'Medio',
      value: 25,
    },
    {
      name: 'Compacto',
      value: 10,
    },
  ];
  hour_segmented_height_ctrl = new FormControl<number>(this.segmentation_options[2].value, { nonNullable: true });
  hour_segmented_height: number = this.segmentation_options[2].value;
  hour_segments: number = 6;

  // Peticions API
  private api_subscriptions: Subscription[] = [];

  // Calendar
  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLElement>;
  events: CalendarEventCustomized[] = [];
  refresh = new Subject<void>();
  dragToCreateActive: boolean = false;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1;
  view: CalendarView = CalendarView.Week;
  calendarUrl: string;

  // Compatibility Availability
  separatorKeysCodes: number[] = [ENTER, COMMA];
  availability_compatibility_users: AvailabilityCompatibilityUserRow[] = [];
  private availability_compatibility_events: CalendarEventCustomized[] = [];
  private pre_dialog_availability_users_snapshot: AvailabilityCompatibilityUserRow[] = [];

  // Calendar Event Detail
  event_selected_dialog_ref: MatDialogRef<CalendarEventDetailDialogComponent> | undefined;
  event_selected: CalendarEventCustomized | undefined;

  // parameters
  filterForm: UntypedFormGroup;
  private formPersistence: any;
  viewDate = signal<Date | null>(null as any);

  readonly viewedWeekState = computed<'past' | 'current' | 'future' | null>(() => {
    const date = this.viewDate();
    if (date == null) return null;
    const viewed = dayjs(date).startOf('isoWeek');
    const current = dayjs().startOf('isoWeek');
    if (viewed.isBefore(current)) return 'past';
    if (viewed.isAfter(current)) return 'future';
    return 'current';
  });

  readonly viewDateForCalendar = computed(() => this.viewDate() ?? dayjs().startOf('isoWeek').toDate());

  readonly viewedWeekSchedulesLoading = signal<boolean>(false);
  readonly viewedWeekSchedulesLoaded = signal<boolean>(false);

  private viewed_week_schedule: WorkingContractWeeklySchedule | null = null;
  private viewed_week_exception: WorkingContractWeeklyScheduleException | null = null;
  private allowed_remote_work_days_resolved: number;
  private readonly availability_enable_promises = new Map<number, Promise<void>>();
  private readonly ocupation_events_load_promises = new Map<number, Promise<void>>();
  private readonly attendee_schedule_cache = new Map<string, CachedAttendeeSchedule>();
  private readonly attendee_schedule_load_promises = new Map<string, Promise<CachedAttendeeSchedule | null>>();
  private readonly attendee_absences_cache = new Map<string, CachedAttendeeAbsences>();
  private readonly attendee_absences_pending_loads = new Map<string, Promise<CachedAttendeeAbsences | null>>();
  private readonly attendee_blocked_events_cache = new Map<string, EmployeeCalendarRegisterWithRelationsSerialized[]>();
  private readonly attendee_blocked_events_pending_loads = new Map<string, Promise<EmployeeCalendarRegisterWithRelationsSerialized[]>>();
  private ocupation_sync_promise: Promise<void> = Promise.resolve();
  private initial_query_params_handled = false;
  private unsaved_changes_prompt: Promise<boolean> | null = null;
  private reverting_filter_to_persistence = false;

  private maxHoursOfMarginToManageEvents: number = 6;

  // Meeting mode
  is_meeting_mode_enabled = false;
  meeting_mode_company_product: null | number = null;
  private readonly privateEventCalendarColor = '#BDBDBD';
  // Import ICS
  @ViewChild('inputFileImportIcs') inputFileImportIcs: ElementRef<HTMLInputElement>;

  constructor() {
    this.filterForm = this.fb.group({
      from: [
        this.activatedRoute.snapshot.queryParamMap.get('from') != null
          ? this.utils.stringToDate(this.activatedRoute.snapshot.queryParamMap.get('from') as string)
          : this.getMonday(new Date()),
      ],
      to: [
        this.activatedRoute.snapshot.queryParamMap.get('to') != null
          ? this.utils.stringToDate(this.activatedRoute.snapshot.queryParamMap.get('to') as string)
          : this.getSunday(new Date()),
      ],
      user_id: [this.activatedRoute.snapshot.queryParamMap.get('user_id') ?? this.userService.getCurrentUser().id],
      ocupation_of_users: [this.parseOcupationOfUsers(this.activatedRoute.snapshot.queryParamMap.get('ocupation_of_users'))],
    });
    this.formPersistence = {
      ...this.filterForm.value,
      ocupation_of_users: this.parseOcupationOfUsers(this.filterForm.value.ocupation_of_users),
    };
    this.viewDate.set(this.filterForm.value['from']);
    this.absences = this.initAbsencesAttribute();

    this.hour_segmented_height_ctrl.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((value: number): void => {
      this.changeHourSegmentedHeight(value);
    });
  }

  async ngOnInit() {
    this.is_meeting_mode_enabled = await firstValueFrom(this.employee.meetingModeEnabled);
    this.meeting_mode_company_product = await firstValueFrom(this.employee.meetingModeCompanyProduct);
    this.me = this.userService.getCurrentUser();
    this.initActiveCompanyProducts();
    await this.fetchNonWorkingTimeCategoriesThatAppearInCalendar();
    await this.fetchEmployees();
    this.initFilterFormListener();
    this.listenQueryParameters();
  }

  castToCalendarEventCustomized(event: CalendarEvent): CalendarEventCustomized {
    return event as CalendarEventCustomized;
  }

  changeHourSegmentedHeight(event: any) {
    this.hour_segmented_height = event;
    this.recalculateEventHeight();
    setTimeout(() => {
      this.refreshEvents();
      this.scrollToStartTime(6);
    }, 100);
  }

  ngAfterViewInit(): void {
    this.scrollToStartTime(7);
  }

  ngOnDestroy(): void {
    if (this.event_selected_dialog_ref) {
      this.event_selected_dialog_ref.close(undefined);
      this.event_selected_dialog_ref = undefined;
      this.event_selected = undefined;
    }
  }

  async openCalendarLinkDialog(): Promise<void> {
    await this.utils.copyTextToClipboard(this.calendarUrl);

    this._snackBar.open('Enlace copiado!', 'Undo', { duration: 3000 });

    this.dialog.open(WorkingTimesCalendarDialogComponent, {
      width: '600px',
      data: { calendarUrl: this.calendarUrl },
    });
  }

  save() {
    const { body, persistedEvents } = this.buildBulkSaveBody();
    if (body.create_or_update.length === 0 && body.delete.length === 0) {
      return;
    }

    return firstValueFrom(this.api.saveBulkEmployeeCalendarRegisters(body)).then(saved => {
      this.applySilentSaveSuccess(persistedEvents, saved ?? []);

      if (dayjs(this.filterForm.value['from']).isSame(dayjs(), 'week') && this.user_selected.id == this.me.id) {
        this.employee.reloadTodaysTasks(this.me);
      }
    });
  }

  canDeactivate(): boolean | Promise<boolean> {
    return this.confirmLeaveUnsavedChanges();
  }

  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedCalendarChanges()) {
      return;
    }

    event.preventDefault();
    event.returnValue = true;
  }

  private hasUnsavedCalendarChanges(): boolean {
    return this.deletable_ids.length > 0 || this.events.some(event => event.meta?.changed === true);
  }

  private hasPersistableUnsavedChanges(): boolean {
    const { body } = this.buildBulkSaveBody();
    return body.create_or_update.length > 0 || body.delete.length > 0;
  }

  private async confirmLeaveUnsavedChanges(): Promise<boolean> {
    if (!this.hasUnsavedCalendarChanges()) {
      return true;
    }

    if (this.unsaved_changes_prompt) {
      return this.unsaved_changes_prompt;
    }

    this.unsaved_changes_prompt = this.openUnsavedChangesDialog();
    try {
      return await this.unsaved_changes_prompt;
    } finally {
      this.unsaved_changes_prompt = null;
    }
  }

  private async openUnsavedChangesDialog(): Promise<boolean> {
    const result = await firstValueFrom(
      this.dialog
        .open(CalendarUnsavedChangesDialogComponent, {
          width: '480px',
          disableClose: true,
          data: { can_save: this.hasPersistableUnsavedChanges() },
        })
        .afterClosed()
    );

    if (result === 'save') {
      try {
        await this.save();
        if (this.hasPersistableUnsavedChanges()) {
          return false;
        }
        this.discardUnsavedCalendarChanges();
        return true;
      } catch {
        return false;
      }
    }

    if (result === 'discard') {
      this.discardUnsavedCalendarChanges();
      return true;
    }

    return false;
  }

  private discardUnsavedCalendarChanges(): void {
    this.reloadWeeklyEvents();
  }

  private revertFilterToPersistence(): void {
    this.reverting_filter_to_persistence = true;
    const persisted = this.formPersistence;
    this.filterForm.patchValue(
      {
        ...persisted,
        ocupation_of_users: this.parseOcupationOfUsers(persisted.ocupation_of_users),
      },
      { emitEvent: false }
    );
    this.viewDate.set(this.getPersistedFromDate());
    this.cdr.detectChanges();
    this.router.navigate(['/employees', 'activity', 'calendar'], {
      queryParams: this.buildQueryParamsFromForm(this.filterForm.value),
    });
    queueMicrotask(() => {
      this.reverting_filter_to_persistence = false;
    });
  }

  private applySilentSaveSuccess(persistedEvents: CalendarEventCustomized[], saved: EmployeeCalendarRegister[]): void {
    this.deletable_ids = [];
    const unused_saved = [...saved];

    for (const event of persistedEvents) {
      const saved_ecr = this.takeMatchingSavedCalendarRegister(event, unused_saved);
      if (saved_ecr?.id != null) {
        event.id = saved_ecr.id;
        event.meta.ecr.id = saved_ecr.id;
        if (saved_ecr.call_link) {
          event.meta.ecr.call_link = saved_ecr.call_link;
        }
        if (saved_ecr.online_conference_id) {
          event.meta.ecr.online_conference_id = saved_ecr.online_conference_id;
        }
        if (saved_ecr.online_conference_type) {
          event.meta.ecr.online_conference_type = saved_ecr.online_conference_type;
        }
        this.mergeSavedSharedUsers(event, saved_ecr);
      }
      event.meta.tmpEvent = false;
      event.meta.notify = undefined;
      event.meta.apply_updates_on = undefined;
      this.markEventAsUnchanged(event);
    }

    this.events = this.events.filter(
      event => !(typeof event.id === 'string' && event.id.startsWith('tmp_') && event.meta.ecr.working_time_category_id == null)
    );

    this.changesAvailableToSave = this.events.some(event => event.meta.changed === true);
    this.refreshEvents();

    if (persistedEvents.some(event => event.meta.ecr.recurrence_freq != null)) {
      void this.fetchUserCalendarRegisters(true);
    }
  }

  private takeMatchingSavedCalendarRegister(
    event: CalendarEventCustomized,
    unused_saved: EmployeeCalendarRegister[]
  ): EmployeeCalendarRegister | undefined {
    const persisted_id = event.meta.ecr.id;
    if (typeof persisted_id === 'number') {
      const index = unused_saved.findIndex(saved_ecr => saved_ecr.id === persisted_id);
      if (index >= 0) {
        return unused_saved.splice(index, 1)[0];
      }
    }

    if (typeof event.id === 'string' && event.id.startsWith('tmp_')) {
      return unused_saved.shift();
    }

    return undefined;
  }

  private mergeSavedSharedUsers(event: CalendarEventCustomized, saved_ecr: EmployeeCalendarRegister): void {
    const saved_shared = (
      saved_ecr as EmployeeCalendarRegister & {
        employee_calendar_register_user_shared_events?: EmployeeCalendarRegisterUserSharedEvent[];
      }
    ).employee_calendar_register_user_shared_events;
    if (!saved_shared?.length) {
      return;
    }

    const local_shared = event.meta.ecr.employee_calendar_register_user_shared_events ?? [];
    event.meta.ecr.employee_calendar_register_user_shared_events = local_shared.map(local => {
      const match = saved_shared.find(
        saved_user =>
          (local.user_id != null && saved_user.user_id === local.user_id) ||
          (local.anonymous_user_email != null && saved_user.anonymous_user_email === local.anonymous_user_email)
      );
      return match?.id != null ? { ...local, id: match.id } : local;
    });
  }

  private buildBulkSaveBody(): {
    body: {
      create_or_update: EmployeeCalendarRegisterWithRelationsSerialized[];
      delete: { id: number; apply_updates_on: 'next' | 'this' | undefined; notify: boolean }[];
    };
    persistedEvents: CalendarEventCustomized[];
  } {
    const body: {
      create_or_update: EmployeeCalendarRegisterWithRelationsSerialized[];
      delete: { id: number; apply_updates_on: 'next' | 'this' | undefined; notify: boolean }[];
    } = {
      create_or_update: [],
      delete: this.deletable_ids,
    };
    const persistedEvents: CalendarEventCustomized[] = [];

    for (const event of this.events) {
      const to_date =
        event.end ??
        dayjs(event.start)
          .add(1 / this.hour_segments, 'hours')
          .toDate();

      if (event.meta.changed !== true) {
        continue;
      }

      const item = {
        ...event.meta.ecr,
        from_date: event.start,
        to_date: to_date,
        notify: event.meta.notify,
        employee_calendar_register_user_shared_events: (event.meta.ecr.employee_calendar_register_user_shared_events ?? []).map(
          shared_user => ({
            id: shared_user.id,
            user_id: shared_user.user_id ?? null,
            anonymous_user_email: shared_user.anonymous_user_email || null,
            working_time_category_id: shared_user.working_time_category_id ?? null,
            relatable_type: shared_user.relatable_type || null,
            relatable_id: shared_user.relatable_id || null,
          })
        ),
      } as EmployeeCalendarRegisterWithRelationsSerialized & {
        notify?: boolean;
        apply_updates_on?: 'this' | 'next';
      };

      if (item.relatable_id == null || (item.relatable_id as unknown) === '') {
        item.relatable_id = undefined;
        item.relatable_type = item.relatable_type || undefined;
      }
      if (!item.online_conference_type) {
        item.online_conference_type = undefined;
      }
      if (!item.call_link) {
        item.call_link = undefined;
      }

      if (item.recurrence_freq && item.recurrence_interval == null) {
        item.recurrence_interval = 1;
      }

      if (typeof event.id === 'string' && event.id.startsWith('tmp_')) {
        delete (item as { id?: number }).id;
      } else {
        item.apply_updates_on = event.meta.apply_updates_on ?? 'this';
        const loaded_user_id = event.meta.loaded_user_id ?? null;
        const next_user_id = item.user_id ?? null;
        if (!isAllowedEmployeeCalendarRegisterUserIdPayloadChange(loaded_user_id, next_user_id, this.me.id)) {
          delete (item as { user_id?: number }).user_id;
        } else if (next_user_id != null) {
          item.user_id = this.me.id;
        }
      }

      const isNewWithoutCategory = !item.id && item.working_time_category_id == null;
      if (!isNewWithoutCategory) {
        body.create_or_update.push(item);
        persistedEvents.push(event);
      }
    }

    return { body, persistedEvents };
  }

  async eventTimesChanged({ event, newStart, newEnd }: CalendarEventTimesChangedEvent): Promise<void> {
    if (event.meta?.is_availability_view_mode) {
      this.refreshEvents();
      return;
    }

    const now: dayjs.Dayjs = dayjs();

    if (dayjs(newStart).isBefore(now.subtract(this.maxHoursOfMarginToManageEvents, 'hours'))) {
      alert(`No puedes arrastrar un evento a antes de ${this.maxHoursOfMarginToManageEvents}h antes de ahora.`);
      this.refreshEvents();
      return;
    }

    const ecr: EmployeeCalendarRegisterWithRelationsSerialized = event.meta.ecr;
    const old_from_date = ecr.from_date;
    const old_to_date = ecr.to_date;
    const new_from_date = newStart ?? old_from_date;
    const new_to_date = newEnd ?? old_to_date;

    const conflicts = await this.getAttendeeCalendarConflicts(
      new_from_date,
      new_to_date,
      this.getAttendeeUsersFromEcr(ecr),
      ecr.all_day,
      ecr.id,
      undefined
    );

    if (!(await this.askProceedDespiteAttendeeConflicts(conflicts))) {
      ecr.from_date = old_from_date;
      ecr.to_date = old_to_date;
      event.start = new Date(old_from_date);
      event.end = new Date(old_to_date);
      this.refreshEvents();
      return;
    }

    if (newStart) ecr.from_date = newStart;
    if (newEnd) ecr.to_date = newEnd;
    const index: number | undefined = event.id != null ? this.events.findIndex(evt => evt.id == event.id) : undefined;

    if (index != undefined) {
      const success = await this.handleEventUpdate(this.events[index], index, ecr);
      this.setTotalHoursCreated();
      if (success) {
        await this.save();
      }
    }
  }

  getAttendeeUsersFromEcr(ecr: EmployeeCalendarRegisterWithRelationsSerialized): User[] {
    return (ecr.employee_calendar_register_user_shared_events ?? [])
      .filter((shared: EmployeeCalendarRegisterUserSharedEvent): boolean => shared.user_id !== null && shared.user !== null)
      .map((shared: EmployeeCalendarRegisterUserSharedEvent): User => shared.user!);
  }

  async getAttendeeCalendarConflicts(
    from_date: Date,
    to_date: Date,
    attendee_users: User[],
    all_day: boolean,
    exclude_event_id?: number | null,
    only_user_ids?: number[]
  ): Promise<AttendeeCalendarConflict[]> {
    let users = attendee_users;

    if (only_user_ids != null && only_user_ids.length > 0) {
      users = users.filter((user: User): boolean => only_user_ids.includes(user.id));
    }

    if (users.length === 0) {
      return [];
    }

    const new_event_is_all_day = !!all_day || (all_day == null && this.isAllDayEventRange(from_date, to_date));
    const conflicts: AttendeeCalendarConflict[] = [];

    for (const user of users) {
      const user_events = await this.fetchAttendeeCalendarEventsForUser(user.id);
      let has_time_off = await this.attendeeHasTimeOffOverlapping(user, from_date, to_date, exclude_event_id);

      for (const other_ecr of user_events) {
        if (exclude_event_id != null && other_ecr.id === exclude_event_id) {
          continue;
        }

        if (!this.intervalsOverlap(from_date, to_date, other_ecr.from_date, other_ecr.to_date)) {
          continue;
        }

        if (other_ecr.all_day) {
          if (this.isAllDayAbsence(other_ecr)) {
            has_time_off = true;
          }
          continue;
        }

        if (new_event_is_all_day) {
          continue;
        }

        conflicts.push({ user, reason: AttendeeCalendarConflictReason.BlockedEvent, conflicting_event: other_ecr });
      }

      if (has_time_off) {
        conflicts.push({ user, reason: AttendeeCalendarConflictReason.TimeOff });
        continue;
      }

      if (!new_event_is_all_day && (await this.isAttendeeOutOfWorkingHours(user, from_date, to_date))) {
        conflicts.push({ user, reason: AttendeeCalendarConflictReason.OutOfHours });
      }
    }

    return conflicts;
  }

  async askProceedDespiteAttendeeConflicts(conflicts: AttendeeCalendarConflict[]): Promise<boolean> {
    if (conflicts.length === 0) {
      return true;
    }

    const conflict_messages = conflicts.map((conflict: AttendeeCalendarConflict): string => {
      const name = `${conflict.user.name} ${conflict.user.surnames}`.trim();

      if (conflict.reason === AttendeeCalendarConflictReason.OutOfHours) {
        return `${name} está fuera de horario.`;
      }

      if (conflict.reason === AttendeeCalendarConflictReason.TimeOff) {
        return `${name} no trabaja ese día.`;
      }

      const start_time = dayjs(conflict.conflicting_event.from_date).format('HH:mm');
      const end_time = dayjs(conflict.conflicting_event.to_date).format('HH:mm');
      return `${name} tiene otro evento de ${start_time} a ${end_time}.`;
    });

    const dialog_ref = this.dialog.open(AttendeeCalendarConflictsDialogComponent, {
      width: '480px',
      data: { conflict_messages },
    });

    const proceed: boolean | undefined = await firstValueFrom(dialog_ref.afterClosed());

    return proceed === true;
  }

  beforeWeekViewRender(renderEvent: CalendarWeekViewBeforeRenderEvent) {
    if (!this.active_contract || !this.viewedWeekSchedulesLoaded() || !this.viewed_week_schedule) {
      return;
    }

    const availability_compatibility_user_rows: AvailabilityCompatibilityUserRow[] = [
      {
        user: this.user_selected,
        color: '',
        absences: this.absences,
        weekly_schedule: this.viewed_week_schedule,
        weekly_schedule_exception: this.viewed_week_exception,
      },
      ...this.availability_compatibility_users,
    ];

    renderEvent.hourColumns.forEach(hourColumn => {
      const day_index: number = hourColumn.date.getDay();

      for (const hour of hourColumn.hours) {
        for (const segment of hour.segments) {
          const hours = segment.date.getHours();
          const minutes = segment.date.getMinutes();
          const hours_ponderated = hours + (minutes === 0 ? 0 : minutes / 60);

          if (this.worksOnDayAndTime(hours_ponderated, day_index)) {
            segment.cssClass = 'contractable';
          } else {
            segment.cssClass = '';
          }

          for (const row of this.availability_compatibility_users) {
            if (row.user.id === this.user_selected.id) {
              continue;
            }
            if (row.weekly_schedule == null) {
              continue;
            }
            if (row.absences[day_index] != null) {
              continue;
            }
            const colleague_periods = this.getScheduleDayPeriodsForDay(day_index, row.weekly_schedule, row.weekly_schedule_exception);
            if (colleague_periods.length === 0) {
              continue;
            }
            if (!this.worksOnDayAndTimeFromPeriods(hours_ponderated, colleague_periods) && segment.cssClass === 'contractable') {
              segment.cssClass = '';
            }
          }

          for (const row of availability_compatibility_user_rows) {
            const absence = row.absences[day_index];
            if (absence != null) {
              segment.cssClass = absence;
            }
          }
        }
      }
    });
  }

  startDragToCreate(segment: WeekViewHourSegment, mouseDownEvent: MouseEvent, segmentElement: HTMLElement) {
    if (!this.event_selected_dialog_ref) {
      const now: dayjs.Dayjs = dayjs();
      if (dayjs(segment.date).isBefore(now.subtract(this.maxHoursOfMarginToManageEvents, 'hours'))) {
        alert(`No puedes arrastrar un evento a antes de ${this.maxHoursOfMarginToManageEvents}h antes de ahora.`);
        this.refreshEvents();
        return;
      }

      if (this.user_selected?.id == this.me.id) {
        const dragToSelectEvent: CalendarEventCustomized = this.createCalendarEventWithNullCategory(segment);

        this.events = [...this.events, dragToSelectEvent];

        this.changesAvailableToSave = true;

        const segmentPosition = segmentElement.getBoundingClientRect();
        this.dragToCreateActive = true;
        const endOfView = endOfWeek(this.filterForm.value['from'], {
          weekStartsOn: this.weekStartsOn,
        });

        fromEvent(document, 'mousemove')
          .pipe(
            finalize(() => {
              delete dragToSelectEvent.meta.tmpEvent;
              this.dragToCreateActive = false;
              this.refreshEvents();
            }),
            takeUntil(fromEvent(document, 'mouseup'))
          )
          .subscribe((mouseMoveEvent: any) => {
            const minutesDiff = ceilToNearest(mouseMoveEvent.clientY - segmentPosition.top, this.hour_segmented_height);

            const daysDiff = floorToNearest(mouseMoveEvent.clientX - segmentPosition.left, segmentPosition.width) / segmentPosition.width;

            const newEnd = addDays(addMinutes(segment.date, minutesDiff), daysDiff);
            if (newEnd > segment.date && newEnd < endOfView) {
              dragToSelectEvent.end = newEnd;
              if (dragToSelectEvent.meta?.ecr) {
                dragToSelectEvent.meta.ecr.to_date = newEnd as any;
              }
            }

            this.refreshEvents();
          });
      }
    }
  }

  checkPermissions(events: any, currentEvent: any = null) {
    const calendar_event = currentEvent?.event ?? currentEvent;
    if (calendar_event?.meta?.is_availability_view_mode) {
      return false;
    }

    if (currentEvent != null) {
      const selected_event = currentEvent?.meta?.ecr ?? currentEvent?.event?.meta?.ecr;
      if (selected_event) {
        const avail_key = generateAvailKeyFromEcr(selected_event);
        if (avail_key != null) {
          const avail = this.availability[avail_key];
          if (avail) {
            if (avail.name.company_product_id) {
              const curentCompanyProduct = this.active_company_products.find(p => p.company_product_id == avail.name?.company_product_id);
              if (curentCompanyProduct?.slug == 'tech-lead') {
                // todo check if user needs to be company_product manager or other roles
                return true;
              }
            }
          }
        }
      } else {
        // no selected_event
      }
    }
    //Todo check if user has permissions on this event
    return this.user_selected?.id == this.me.id || this.permissions.validateDepartment(['direction', 'rrhh']) ? true : false;
  }

  onClickCreateNewEvent() {
    if (!this.event_selected_dialog_ref) {
      this.pre_dialog_availability_users_snapshot = [...this.availability_compatibility_users];
      const current_week_date: Date = new Date(this.filterForm.value.from);
      this.event_selected_dialog_ref = this.dialog.open(CalendarEventDetailDialogComponent, {
        hasBackdrop: false,
        data: {
          event: undefined,
          availability: this.availability,
          calendarComponent: this,
          user_id: this.user_selected.id,
          non_working_time_categories_that_appear_in_calendar: this.non_working_time_categories_that_appear_in_calendar,
          updatable: true,
        },
        position: {
          right: '100px',
        },
        width: '500px',
      });

      this.event_selected_dialog_ref.afterClosed().subscribe((data: EmployeeCalendarRegisterWithRelationsSerialized | undefined) => {
        this.onEventDialogClosed(data, current_week_date);
      });
    }
  }

  async eventClicked(event_clicked: CalendarEventCustomized) {
    const company_product_id = event_clicked.meta.ecr.company_product_id;
    const is_meeting_mode = this.is_meeting_mode_enabled && this.meeting_mode_company_product !== company_product_id;
    const has_details_access = event_clicked.meta.ecr.has_details_access !== false;
    if (!this.event_selected_dialog_ref && !is_meeting_mode && has_details_access) {
      const old_event: CalendarEventCustomized = { ...event_clicked };
      this.event_selected = event_clicked;
      const ecr: EmployeeCalendarRegisterWithRelationsSerialized | undefined = event_clicked.meta.ecr;

      this.pre_dialog_availability_users_snapshot = [...this.availability_compatibility_users];
      for (const ecruse of ecr?.employee_calendar_register_user_shared_events ?? []) {
        if (ecruse.user) {
          void this.enableAvailabilityPreviewForUser(ecruse.user);
        }
      }
      this.floatEvent(event_clicked);
      this.event_selected_dialog_ref = this.dialog.open(CalendarEventDetailDialogComponent, {
        hasBackdrop: false,
        data: {
          event: event_clicked,
          availability: this.availability,
          calendarComponent: this,
          user_id: this.user_selected.id,
          non_working_time_categories_that_appear_in_calendar: this.non_working_time_categories_that_appear_in_calendar,
          updatable: dayjs(event_clicked.start).isAfter(dayjs().subtract(this.maxHoursOfMarginToManageEvents, 'hours')),
        },
        position: {
          right: '100px',
        },
        width: '500px',
      });

      this.event_selected_dialog_ref.afterClosed().subscribe((data: CalendarEventDetailDialogCloseResult) => {
        this.event_selected_dialog_ref = undefined;
        if (data && typeof data === 'object' && 'calendar_dialog_action' in data) {
          if (data.calendar_dialog_action === 'delete') {
            void this.onDelete(old_event);
          }
          if (this.event_selected != null) this.unfloatEvent(this.event_selected);
          this.event_selected = undefined;
          return;
        }

        this.onEventDialogClosed(data, old_event.start, old_event);
      });
    }
  }

  async onChangeWeekClicked(from: Date): Promise<void> {
    if (!(await this.confirmLeaveUnsavedChanges())) {
      this.viewDate.set(this.filterForm.value['from']);
      this.cdr.detectChanges();
      return;
    }

    this.changeWeek(from);
  }

  openConfigureNormalSchedule(): void {
    if (!this.active_contract || !this.viewed_week_schedule) return;

    const from = dayjs(this.filterForm.value['from']).startOf('isoWeek');
    const to = dayjs(this.filterForm.value['to']).endOf('isoWeek');
    const data: ConfigureWorkingScheduleDialogData = {
      working_contract: this.active_contract,
      weekly_schedule: this.viewed_week_schedule,
      schedule_mode: ScheduleMode.Normal,
      start_week_date: from.format('YYYY-MM-DD'),
      end_week_date: to.format('YYYY-MM-DD'),
      dialog_title: 'Configurar horario habitual',
      dialog_subtitle: `Activo a partir del ${from.format('DD/MM/YYYY')}`,
      allowed_remote_work_days_resolved: this.allowed_remote_work_days_resolved,
    };

    this.openConfigureScheduleDialog(data);
  }

  openConfigureThisWeekSchedule(): void {
    if (!this.active_contract || !this.viewed_week_schedule) return;

    const from = dayjs(this.filterForm.value['from']).startOf('isoWeek');
    const to = dayjs(this.filterForm.value['to']).endOf('isoWeek');
    const data: ConfigureWorkingScheduleDialogData = {
      working_contract: this.active_contract,
      weekly_schedule: this.viewed_week_schedule,
      weekly_schedule_exception: this.viewed_week_exception ?? undefined,
      schedule_mode: ScheduleMode.WeekException,
      start_week_date: from.format('YYYY-MM-DD'),
      end_week_date: to.format('YYYY-MM-DD'),
      dialog_title: 'Configurar horario de esta semana',
      dialog_subtitle: `Semana del ${from.format('DD/MM/YYYY')} al ${to.format('DD/MM/YYYY')}`,
      week_blocked_meeting_ecrs: this.getBlockedMeetingEcrsForViewedWeek(),
      week_absence_days: this.getWeekAbsenceDays(),
      allowed_remote_work_days_resolved: this.allowed_remote_work_days_resolved,
    };

    this.openConfigureScheduleDialog(data);
  }

  async enableAvailabilityViewForUser(user: User, options?: { allow_without_ocupation?: boolean }) {
    if (!this.shouldLoadOcupationDataForUser(user.id, options?.allow_without_ocupation)) {
      return;
    }

    if (this.hasAvailabilityViewEnabledForUser(user)) {
      return;
    }

    const existing = this.availability_enable_promises.get(user.id);
    if (existing) {
      await existing;
      return;
    }

    const promise = this.doEnableAvailabilityViewForUser(user, options);
    this.availability_enable_promises.set(user.id, promise);
    try {
      await promise;
    } finally {
      this.availability_enable_promises.delete(user.id);
    }
  }

  async enableAvailabilityPreviewForUser(user: User): Promise<void> {
    await this.enableAvailabilityViewForUser(user, { allow_without_ocupation: true });
    const row = this.availability_compatibility_users.find(r => +r.user.id === +user.id);
    if (row) await this.loadOcupationEventsForRow(row, true, { allow_without_ocupation: true });
  }

  hasAvailabilityViewEnabled() {
    return this.availability_compatibility_users.length > 0;
  }

  hasAvailabilityViewEnabledForUser(user: User) {
    return this.availability_compatibility_users.find(u => u.user.id == user.id) !== undefined;
  }

  disableAvailabilityViewForUser(user: User | undefined = undefined) {
    if (user === undefined) {
      this.availability_compatibility_events = [];
      this.availability_compatibility_users = [];
      this.events = this.events.filter(event => event.meta.is_availability_view_mode !== true);
      this.refreshEvents();

      return;
    }

    const user_id = user.id;

    this.availability_compatibility_events = this.availability_compatibility_events.filter(
      event => !this.isAvailabilityEventForUserId(event, user_id)
    );
    this.events = this.events.filter(event => !this.isAvailabilityEventForUserId(event, user_id));

    const availability_user_index = this.availability_compatibility_users.findIndex(row => row.user.id === user_id);
    if (availability_user_index > -1) {
      this.availability_compatibility_users.splice(availability_user_index, 1);
    }

    this.refreshEvents();
  }

  onSelectedEventModified(ecr: EmployeeCalendarRegisterWithRelationsSerialized) {
    // Preservar propiedades esenciales del ECR original si están disponibles
    const originalEcr = this.event_selected?.meta?.ecr;
    if (originalEcr) {
      // Solo preservar si el nuevo ECR no tiene estas propiedades
      if (!ecr.relatable_type && originalEcr.relatable_type) {
        ecr.relatable_type = originalEcr.relatable_type;
      }
      if (!ecr.relatable_id && originalEcr.relatable_id) {
        ecr.relatable_id = originalEcr.relatable_id;
      }

      // Check if company_product/phase changed - if so, use new values, otherwise preserve old ones
      const companyProductChanged = ecr.relatable_id !== originalEcr.relatable_id || ecr.relatable_type !== originalEcr.relatable_type;

      if (!companyProductChanged) {
        // CompanyProduct hasn't changed, preserve old values if new ones are missing
        if (!ecr.company_product_color && originalEcr.company_product_color) {
          ecr.company_product_color = originalEcr.company_product_color;
        }
        if (!ecr.company_product_title && originalEcr.company_product_title) {
          ecr.company_product_title = originalEcr.company_product_title;
        }
        if (!ecr.company_product_phase_name && originalEcr.company_product_phase_name) {
          ecr.company_product_phase_name = originalEcr.company_product_phase_name;
        }
      }
      // If company_product changed, use the new values from ecr (which should come from the dialog)

      // Si el usuario cambió la categoría, usar las propiedades de la nueva categoría del diálogo
      if (ecr.working_time_category_id !== originalEcr.working_time_category_id) {
        // El diálogo ya envía las propiedades completas de la nueva categoría
        if (ecr.working_time_category) {
          ecr.wtc_name = ecr.working_time_category.name;
          ecr.wtc_color = ecr.working_time_category.color;
        }
      } else {
        // Solo preservar wtc_name si el usuario no cambió la categoría de trabajo
        if (!ecr.wtc_name && originalEcr.wtc_name) {
          ecr.wtc_name = originalEcr.wtc_name;
        }
        // Solo preservar wtc_color si el usuario no cambió la categoría de trabajo
        if (!ecr.wtc_color && originalEcr.wtc_color) {
          ecr.wtc_color = originalEcr.wtc_color;
        }
        // Solo preservar working_time_category si el usuario no cambió la categoría de trabajo
        if (!ecr.working_time_category && originalEcr.working_time_category) {
          ecr.working_time_category = originalEcr.working_time_category;
        }
      }

      if (!ecr.trip_driver_user_id && originalEcr.trip_driver_user_id) {
        ecr.trip_driver_user_id = originalEcr.trip_driver_user_id;
      }
    }

    const event: CalendarEventCustomized = this.ecrToCalendarEventCustomized(ecr);
    const event_selected_until_now = this.event_selected ?? this.events.find(evt => evt.id == event.id);
    event.meta.loaded_user_id =
      event_selected_until_now?.meta.loaded_user_id ?? this.event_selected?.meta.loaded_user_id ?? ecr.user_id ?? null;

    if (event_selected_until_now) {
      const idx = this.events.indexOf(event_selected_until_now);
      if (idx > -1) this.events.splice(idx, 1);
    }

    this.event_selected = event;
    this.floatEvent(this.event_selected);
    this.events.push(this.event_selected);
    this.refreshEvents();
  }

  async onClickToday(): Promise<void> {
    if (!(await this.confirmLeaveUnsavedChanges())) {
      return;
    }

    this.changeWeek(new Date());
  }

  onRemoveAvailabilityCompatibilityUser(user: User) {
    // this.workers_ctrl.patchValue('');
    this.disableAvailabilityViewForUser(user);
  }

  compareOcupationUserIds = (id_a: number | string | null, id_b: number | string | null): boolean => {
    if (id_a == null || id_b == null) return id_a === id_b;
    return +id_a === +id_b;
  };

  onClickImportIcs() {
    this.inputFileImportIcs.nativeElement.click();
  }

  onFileSelectedImportIcs(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const file: File = input.files[0];
      this.processFileImportCsv(file);
    }
  }

  onDropIcsFile(event: NgxDropzoneChangeEvent) {
    for (const file of event.addedFiles) {
      this.processFileImportCsv(file);
    }
  }

  openRemoteWorkDialog() {
    this.api.getEmployeeRemoteWork(this.me.id.toString(), this.filterForm.value['from'], this.filterForm.value['to']).subscribe(data => {
      data.forEach((day: any) => {
        const day_name = dayjs(day).locale('en').format('dddd').toLowerCase() as DayOfWeekEnum;

        if (day_name in this.remote_work_days) {
          this.remote_work_days[day_name] = true;
        }
      });

      this.dialog.open(ManageRemoteWorkDialogComponent, {
        width: '500px',
        data: {
          remote_work_days: { ...this.remote_work_days },
          user_id: this.me.id.toString(),
          from: this.filterForm.value['from'],
          to: this.filterForm.value['to'],
        },
        autoFocus: false,
      });
    });
  }

  isPastWeek(): boolean {
    const to = dayjs(this.filterForm.value['to']);
    const today = dayjs();
    const start_of_week = today.startOf('isoWeek');

    return to.isBefore(start_of_week, 'day');
  }

  private async doEnableAvailabilityViewForUser(user: User, options?: { allow_without_ocupation?: boolean }) {
    if (!this.shouldLoadOcupationDataForUser(user.id, options?.allow_without_ocupation)) {
      return;
    }

    if (this.hasAvailabilityViewEnabledForUser(user)) {
      return;
    }

    const start_week_monday = dayjs(this.filterForm.value['from']).startOf('isoWeek').format('YYYY-MM-DD');
    const schedule = await this.getCachedAttendeeScheduleForWeek(user, start_week_monday);
    const weekly_schedule = schedule?.weekly_schedule ?? null;
    const weekly_schedule_exception = schedule?.weekly_schedule_exception ?? null;

    if (!this.shouldLoadOcupationDataForUser(user.id, options?.allow_without_ocupation)) {
      return;
    }

    const user_row: AvailabilityCompatibilityUserRow = {
      user: user,
      color: this.utils.generateRandomColor(),
      absences: (await this.fetchAbsences(user.id, [null, null, null, null, null, null, null]))!,
      weekly_schedule,
      weekly_schedule_exception,
    };

    if (this.hasAvailabilityViewEnabledForUser(user)) {
      return;
    }

    this.availability_compatibility_users.push(user_row);
  }

  private isAvailabilityEventForUserId(event: CalendarEventCustomized, user_id: number): boolean {
    const event_user_id = event.meta.availability_view_user_id ?? event.meta.ecr?.user_id;
    return event.meta.is_availability_view_mode === true && event_user_id != null && +event_user_id === +user_id;
  }

  private isUserInOcupationSelection(user_id: number): boolean {
    return this.parseOcupationOfUsers(this.filterForm.value.ocupation_of_users).some(id => +id === +user_id);
  }

  private shouldLoadOcupationDataForUser(user_id: number, allow_without_ocupation = false): boolean {
    return allow_without_ocupation || this.isUserInOcupationSelection(user_id);
  }

  private setOcupationOfUsersSelected(ids: number[]): void {
    const next_ids = this.parseOcupationOfUsers(ids);
    this.filterForm.patchValue({ ocupation_of_users: next_ids }, { emitEvent: false });
  }

  private parseOcupationOfUsers(value: unknown): number[] {
    if (value == null || value === '') return [];
    if (Array.isArray(value)) {
      return value.map(v => +v).filter(id => !isNaN(id) && id > 0);
    }
    if (typeof value === 'number') {
      return !isNaN(value) && value > 0 ? [value] : [];
    }
    return String(value)
      .split(',')
      .map(id => +id.trim())
      .filter(id => !isNaN(id) && id > 0);
  }

  private serializeOcupationOfUsers(ids: unknown): string | null {
    const parsed = this.parseOcupationOfUsers(ids);
    return parsed.length > 0 ? parsed.join(',') : null;
  }

  private buildQueryParamsFromForm(form_value: Record<string, unknown>): Record<string, string | number> {
    const params: Record<string, string | number> = {
      user_id: form_value['user_id'] as number,
    };

    if (form_value['from'] != null && form_value['from'] !== '') {
      params.from = this.utils.dateToString(new Date(form_value['from'] as Date));
    }

    if (form_value['to'] != null && form_value['to'] !== '') {
      params.to = this.utils.dateToString(new Date(form_value['to'] as Date));
    }

    const ocupation_of_users = this.serializeOcupationOfUsers(form_value['ocupation_of_users']);
    if (ocupation_of_users != null) {
      params.ocupation_of_users = ocupation_of_users;
    }

    return params;
  }

  private applyOcupationSelectionChange(previous_ids: number[], target_ids: number[]): Promise<void> {
    this.ocupation_sync_promise = this.ocupation_sync_promise
      .then(() => this.syncOcupationSelectionDelta(previous_ids, target_ids))
      .catch(() => undefined);
    return this.ocupation_sync_promise;
  }

  private async syncOcupationSelectionDelta(previous_ids: number[], target_ids: number[]): Promise<void> {
    if (!this.userOptions.length) return;

    const previous_set = new Set(previous_ids.filter(id => id !== this.me?.id).map(id => +id));
    const target_set = new Set(
      this.parseOcupationOfUsers(target_ids)
        .filter(id => id !== this.me?.id)
        .map(id => +id)
    );

    if (target_set.size === 0) {
      this.disableAvailabilityViewForUser();
      return;
    }

    const removed_ids = [...previous_set].filter(id => !target_set.has(id));
    const added_ids = [...target_set].filter(id => !previous_set.has(id));

    for (const id of removed_ids) {
      const user = this.userOptions.find(u => +u.id === id);
      if (user) this.disableAvailabilityViewForUser(user);
    }

    for (const id of added_ids) {
      const user = this.userOptions.find(u => +u.id === id);
      if (!user) continue;

      await this.enableAvailabilityViewForUser(user);
      const row = this.availability_compatibility_users.find(r => +r.user.id === id);
      if (row) await this.loadOcupationEventsForRow(row);
    }
  }

  private async syncOcupationUsersFromForm(): Promise<void> {
    const target_ids = this.parseOcupationOfUsers(this.filterForm.value.ocupation_of_users);
    const current_ids = this.availability_compatibility_users.map(row => +row.user.id);
    await this.syncOcupationSelectionDelta(current_ids, target_ids);
  }

  private fetchRemoteWorkingDays() {
    const subscription = this.api
      .getEmployeeRemoteWork(this.user_selected.id.toString(), this.filterForm.value['from'], this.filterForm.value['to'])
      .subscribe(
        data => {
          data.forEach((item: any) => {
            // add date as event all day to calendar
            this.events.push({
              start: new Date(item),
              title: '🏠',
              color: {
                primary: 'transparent',
                secondary: 'transparent',
              },
              allDay: true,
              meta: {
                tmpEvent: false,
                changed: false,
                hide_tooltip: true,
              },
            } as CalendarEventCustomized);
            this.refreshEvents();
          });
        },
        error => {},
        () => {
          this.api_subscriptions.splice(
            this.api_subscriptions.findIndex(s => s === subscription),
            1
          );
        }
      );

    this.api_subscriptions.push(subscription);
  }

  private async fetchEmployees() {
    try {
      const data = await firstValueFrom(
        this.api.getActiveEmployees({
          from: dayjs(this.filterForm.value['from']).format('YYYY-MM-DD'),
          to: dayjs(this.filterForm.value['to']).format('YYYY-MM-DD'),
        })
      );
      this.userOptions = data;
      this.setOcupationOfUsersSelected(this.parseOcupationOfUsers(this.filterForm.value.ocupation_of_users));
      const user_selected = this.userOptions.find(u => u.id == this.filterForm.value.user_id) as User;
      if (user_selected) {
        await this.selectUser(user_selected.id);
      } else {
        await this.syncOcupationUsersFromForm();
      }
    } catch {
      // keep calendar usable if the employee list request fails
    }
  }

  private fetchUserWeeklyAvailability() {
    // Simplified to work only with working time categories
    this.initAvailability();
    if (this.active_contract == null) {
      this.fetchUserContract();
      return;
    }
    this.refreshViewedWeekSchedules((): void => {
      this.refreshEvents();
    });
  }

  private fetchUserContract(): void {
    this.active_contract = null;
    this.viewed_week_schedule = null;
    this.viewed_week_exception = null;
    this.viewedWeekSchedulesLoaded.set(false);

    const params: any = {
      day: this.utils.dateToStringYYYYMMDD(this.filterForm.value['from']),
      // El único objetivo de este with es obtener el hours_per_week_expected real para poder ajustar máximos y mínimos de horas por día según horas de contrato
      with: 'current_working_contract_variable_condition',
    };

    this.api.getActiveWorkingContractInWeek(this.user_selected.id, params).subscribe(data => {
      this.active_contract = data;
      this.refreshViewedWeekSchedules((): void => {
        this.refreshEvents();
      });
    });
  }

  private refreshViewedWeekSchedules(after: () => void): void {
    if (this.active_contract == null) {
      this.viewed_week_schedule = null;
      this.viewed_week_exception = null;
      this.viewedWeekSchedulesLoading.set(false);
      this.viewedWeekSchedulesLoaded.set(false);
      after();
      return;
    }

    const start_week_date = this.utils.dateToStringYYYYMMDD(this.filterForm.value['from']);
    this.viewedWeekSchedulesLoading.set(true);
    this.viewedWeekSchedulesLoaded.set(false);
    this.api
      .getWorkingContractScheduleForWeek(this.active_contract.id, start_week_date)
      .subscribe((response: WorkingContractScheduleForWeekResponse): void => {
        this.viewed_week_schedule = response.working_contract_weekly_schedule;
        this.viewed_week_exception = response.working_contract_weekly_schedule_exception;
        this.viewedWeekSchedulesLoading.set(false);
        this.viewedWeekSchedulesLoaded.set(true);
        this.allowed_remote_work_days_resolved = response.allowed_remote_work_days_resolved;
        after();
      });
  }

  private initAvailability() {
    this.avail_key_selected = null as any;
    this.total_hours_assigned = 0;
    this.availability = {};

    // Initialize availability with non-working time categories that appear in calendar
    this.non_working_time_categories_that_appear_in_calendar.forEach(wtc => {
      const key: string = generateAvailKeyFromEcr({
        working_time_category_id: wtc.id,
      } as any);
      this.availability[key] = {
        name: { title: wtc.name } as any,
        color: wtc.color,
        working_time_category_id: wtc.id,
        working_time_category_slug: wtc.slug,
        is_working_time: wtc.is_working_time,
        hours_to_do: 0,
        hours_assigned: 0,
        relatable_id: undefined,
        relatable_type: undefined,
      };
    });

    this.avail_key_selected = Object.keys(this.availability)[0]; // seleccionem la primera key
  }

  private initActiveCompanyProducts() {
    this.active_company_products = this.employee.getActiveCompanyProducts();
  }

  private async fetchUserCalendarRegisters(replace_existing = false): Promise<void> {
    const requested_from = this.filterForm.value['from'];
    const requested_to = this.filterForm.value['to'];
    const requested_user_id = this.user_selected.id;

    // El SnackbarInterceptor ya muestra el error de API; degradamos a [] para no cortar el reload de la semana.
    const data = await firstValueFrom(
      this.api
        .getEmployeeCalendarRegistersForUserSerialized(requested_user_id.toString(), requested_from, requested_to, {
          with: 'working_time_category',
        })
        .pipe(catchError(() => of([] as EmployeeCalendarRegisterWithRelationsSerialized[])))
    );

    if (
      requested_user_id !== this.user_selected.id ||
      !this.isSameCalendarRange(requested_from, requested_to, this.filterForm.value['from'], this.filterForm.value['to'])
    ) {
      return;
    }

    if (replace_existing) {
      for (const key of Object.keys(this.availability)) {
        this.availability[key].hours_assigned = 0;
      }
    }

    const events = data
      .filter(evt => this.event_selected == null || this.event_selected.id != evt.id)
      .filter(ecr => ecr.wtc_first_level_slug !== 'vacations')
      .map(ecr => {
        const avail_index = generateAvailKeyFromEcr(ecr);
        if (this.availability[avail_index] != null) {
          this.availability[avail_index].hours_assigned += dayjs(ecr.to_date).diff(dayjs(ecr.from_date), 'hours', true);
        }

        return this.ecrToCalendarEventCustomized(ecr);
      });

    if (replace_existing) {
      const preserved = this.events.filter(event => event.meta?.hide_tooltip);
      this.events = [...events, ...preserved];
    } else {
      this.events = [...this.events, ...events];
    }

    const absences_or_undefined = await this.fetchAbsences();
    if (absences_or_undefined) {
      this.absences = absences_or_undefined;
    }

    this.cleanAvailsThatAreNotGoingToBeUsed();
    this.setTotalHoursCreated();
    this.refreshEvents();
  }

  private isSameCalendarRange(from_a: unknown, to_a: unknown, from_b: unknown, to_b: unknown): boolean {
    const start_a = dayjs(from_a as string | Date);
    const end_a = dayjs(to_a as string | Date);
    const start_b = dayjs(from_b as string | Date);
    const end_b = dayjs(to_b as string | Date);

    return (
      start_a.isValid() &&
      end_a.isValid() &&
      start_b.isValid() &&
      end_b.isValid() &&
      start_a.isSame(start_b, 'day') &&
      end_a.isSame(end_b, 'day')
    );
  }

  private isSameRecurrenceSeries(
    event_ecr: EmployeeCalendarRegisterWithRelationsSerialized,
    other_ecr: EmployeeCalendarRegisterWithRelationsSerialized
  ): boolean {
    const event_series_id = event_ecr.parent_recurrent_employee_calendar_register_id ?? event_ecr.id;
    const other_series_id = other_ecr.parent_recurrent_employee_calendar_register_id ?? other_ecr.id;

    return event_series_id != null && other_series_id != null && event_series_id === other_series_id;
  }

  private calculateDifferenceInMinutes(date1: Date, date2: Date): number {
    const differenceInMilliseconds = Math.abs(date2.getTime() - date1.getTime());
    const differenceInMinutes = Math.floor(differenceInMilliseconds / (1000 * 60));
    return differenceInMinutes;
  }

  private calculateHeightOfEvent(minutes: number): number {
    return ((minutes * this.hour_segments) / 60) * this.hour_segmented_height;
  }

  private recalculateEventHeight() {
    this.events.forEach(event => {
      if (event.end != null) {
        const h = this.calculateHeightOfEvent(this.calculateDifferenceInMinutes(event.start, event.end) ?? 5);
        event.height = h;
      }
    });
    this.refreshEvents();
  }

  private async fetchAbsences(
    user_id: number | undefined = undefined,
    custom_user_absences_array: ('holiday' | 'vacation' | 'other' | null)[] | undefined = undefined
  ): Promise<('holiday' | 'vacation' | 'other' | null)[] | undefined> {
    let absences = custom_user_absences_array ?? this.absences; // agafa el de l'usuari si li passen sino
    const data = await this.getCachedAttendeeAbsencesForViewedWeek(user_id ?? this.user_selected.id);

    if (data === null) {
      return undefined;
    }

    absences = this.initAbsencesAttribute(absences);

    data.employee_calendar_registers.forEach((ecr: EmployeeCalendarRegister): void => {
      if (!ecr.all_day) {
        return;
      }

      const index = dayjs(ecr.from_date).day();

      if (absences[index] == null) {
        let str: 'other' | 'vacation' = 'other';
        if (ecr.working_time_category!.slug === 'vacations') str = 'vacation';
        absences[index] = str;
      }
    });

    data.holidays.forEach((holiday: Holiday) => {
      const index = dayjs(holiday.date).day();

      if (absences[index] == null) {
        absences[index] = 'holiday';
      }
    });

    if (!custom_user_absences_array) this.refreshEvents();

    return absences;
  }

  private getScheduleDayPeriodsForIndex(day_index: number): WorkingContractWeeklyScheduleDayPeriod[] {
    return this.getScheduleDayPeriodsForDay(day_index, this.viewed_week_schedule, this.viewed_week_exception);
  }

  private getScheduleDayPeriodsForDay(
    day_index: number,
    schedule: WorkingContractWeeklySchedule | null,
    exception: WorkingContractWeeklyScheduleException | null
  ): WorkingContractWeeklyScheduleDayPeriod[] {
    const iso_day = day_index === 0 ? 7 : day_index;
    let day_enum: DayOfWeekEnum;
    try {
      day_enum = fromDayOfWeekIso(iso_day);
    } catch {
      return [];
    }

    if (exception) {
      return exception.working_contract_weekly_schedule_exception_day_periods.filter(
        p => p.day_of_week === day_enum
      ) as WorkingContractWeeklyScheduleDayPeriod[];
    }

    if (schedule == null) {
      return [];
    }

    return schedule.working_contract_weekly_schedule_day_periods.filter(p => p.day_of_week === day_enum);
  }

  private async isAttendeeOutOfWorkingHours(user: User, from_date: Date, to_date: Date): Promise<boolean> {
    if (this.isAllDayEventRange(from_date, to_date)) {
      return false;
    }

    const schedule = await this.fetchAttendeeScheduleForWeek(user, from_date);

    if (schedule == null) {
      return false;
    }

    const day_index = dayjs(from_date).day();

    const periods = this.getScheduleDayPeriodsForDay(day_index, schedule.weekly_schedule, schedule.weekly_schedule_exception);

    return !this.isIntervalWithinWorkingPeriods(from_date, to_date, periods);
  }

  private isAllDayAbsence(ecr: EmployeeCalendarRegisterWithRelationsSerialized): boolean {
    return !!ecr.all_day && !ecr.is_working_time && !ecr.is_editable_from_calendar;
  }

  private async attendeeHasTimeOffOverlapping(
    user: User,
    from_date: Date,
    to_date: Date,
    exclude_event_id?: number | null
  ): Promise<boolean> {
    const data = await this.getCachedAttendeeAbsencesForViewedWeek(user.id);

    if (data == null) {
      return false;
    }

    const holiday_overlaps = data.holidays.some((holiday: Holiday): boolean => {
      const holiday_day = dayjs(holiday.date);

      if (!holiday_day.isValid()) {
        return false;
      }

      return this.intervalsOverlap(from_date, to_date, holiday_day.startOf('day').toDate(), holiday_day.endOf('day').toDate());
    });

    if (holiday_overlaps) {
      return true;
    }

    return data.employee_calendar_registers.some((ecr: EmployeeCalendarRegister): boolean => {
      if (exclude_event_id != null && ecr.id === exclude_event_id) {
        return false;
      }

      if (!ecr.all_day) {
        return false;
      }

      return this.intervalsOverlap(from_date, to_date, new Date(ecr.from_date), new Date(ecr.to_date));
    });
  }

  private async getCachedAttendeeAbsencesForViewedWeek(user_id: number): Promise<CachedAttendeeAbsences | null> {
    const cache_key = this.attendeeViewedWeekCacheKey(user_id);
    const cached = this.attendee_absences_cache.get(cache_key);
    if (cached !== undefined) {
      return cached;
    }

    const pending_load_promise = this.attendee_absences_pending_loads.get(cache_key);
    if (pending_load_promise) {
      return pending_load_promise;
    }

    const promise = this.loadAttendeeAbsences(user_id, cache_key);
    this.attendee_absences_pending_loads.set(cache_key, promise);
    try {
      return await promise;
    } finally {
      this.attendee_absences_pending_loads.delete(cache_key);
    }
  }

  private async loadAttendeeAbsences(user_id: number, cache_key: string): Promise<CachedAttendeeAbsences | null> {
    const data = await firstValueFrom(
      this.api.getEmployeeAbsences(user_id, this.filterForm.value['from'], this.filterForm.value['to']).pipe(catchError(() => of(null)))
    );

    if (data == null) {
      return null;
    }

    const absences: CachedAttendeeAbsences = {
      holidays: data.holidays,
      employee_calendar_registers: data.employee_calendar_registers,
    };
    this.attendee_absences_cache.set(cache_key, absences);

    return absences;
  }

  private attendeeViewedWeekCacheKey(user_id: number): string {
    const week_from = dayjs(this.filterForm.value['from']).startOf('isoWeek').format('YYYY-MM-DD');
    const week_to = dayjs(this.filterForm.value['to']).endOf('isoWeek').format('YYYY-MM-DD');

    return `${user_id}_${week_from}_${week_to}`;
  }

  private async fetchAttendeeScheduleForWeek(user: User, from_date: Date): Promise<CachedAttendeeSchedule | null> {
    const event_week_monday = dayjs(from_date).startOf('isoWeek').format('YYYY-MM-DD');
    return this.getCachedAttendeeScheduleForWeek(user, event_week_monday);
  }

  private async getCachedAttendeeScheduleForWeek(user: User, week_monday: string): Promise<CachedAttendeeSchedule | null> {
    const role_slug = user.role?.slug;
    if (role_slug === 'partner' || role_slug === 'client') {
      return null;
    }

    const cache_key = this.attendeeScheduleCacheKey(user.id, week_monday);
    const cached = this.attendee_schedule_cache.get(cache_key);
    if (cached) {
      return cached;
    }

    const in_flight_load_promise = this.attendee_schedule_load_promises.get(cache_key);
    if (in_flight_load_promise) {
      return in_flight_load_promise;
    }

    const promise = this.loadAttendeeScheduleForWeek(user.id, week_monday, cache_key);
    this.attendee_schedule_load_promises.set(cache_key, promise);
    try {
      return await promise;
    } finally {
      this.attendee_schedule_load_promises.delete(cache_key);
    }
  }

  private async loadAttendeeScheduleForWeek(
    user_id: number,
    week_monday: string,
    cache_key: string
  ): Promise<CachedAttendeeSchedule | null> {
    const schedule_response = await firstValueFrom(
      this.api.getActiveWorkingContractScheduleForUser(user_id, week_monday).pipe(catchError(() => of(null)))
    );

    if (schedule_response == null) {
      return null;
    }

    const schedule: CachedAttendeeSchedule = {
      weekly_schedule: schedule_response.working_contract_weekly_schedule,
      weekly_schedule_exception: schedule_response.working_contract_weekly_schedule_exception,
    };
    this.attendee_schedule_cache.set(cache_key, schedule);

    return schedule;
  }

  private attendeeScheduleCacheKey(user_id: number, week_monday: string): string {
    return `${user_id}_${week_monday}`;
  }

  private invalidateAttendeeScheduleCache(user_id: number, week_monday: string): void {
    this.attendee_schedule_cache.delete(this.attendeeScheduleCacheKey(user_id, week_monday));
  }

  private isAllDayEventRange(from_date: Date, to_date: Date): boolean {
    const from = dayjs(from_date);
    const to = dayjs(to_date);

    return from.hour() === 0 && from.minute() === 0 && to.hour() === 23 && to.minute() === 59;
  }

  private isIntervalWithinWorkingPeriods(from_date: Date, to_date: Date, periods: WorkingContractWeeklyScheduleDayPeriod[]): boolean {
    if (periods.length === 0) {
      return false;
    }

    const from = dayjs(from_date);
    const to = dayjs(to_date);

    if (!from.isSame(to, 'day')) {
      return false;
    }

    const start_minutes = from.hour() * 60 + from.minute();
    const end_minutes = to.hour() * 60 + to.minute();

    if (end_minutes <= start_minutes) {
      return this.worksOnDayAndTimeFromPeriods(start_minutes / 60, periods);
    }

    const ranges = periods
      .map((period: WorkingContractWeeklyScheduleDayPeriod): { start: number; end: number } => {
        const [start_hour, start_minute] = period.start_time.split(':').map(n => +n);
        const [end_hour, end_minute] = period.end_time.split(':').map(n => +n);
        return { start: start_hour * 60 + (start_minute ?? 0), end: end_hour * 60 + (end_minute ?? 0) };
      })
      .sort((a, b) => a.start - b.start);

    let cursor = start_minutes;

    for (const range of ranges) {
      if (range.end <= cursor) {
        continue;
      }

      if (range.start > cursor) {
        return false;
      }

      cursor = Math.max(cursor, range.end);

      if (cursor >= end_minutes) {
        return true;
      }
    }

    return cursor >= end_minutes;
  }

  private async scrollToStartTime(hour_decimal: number) {
    this.scrollContainer.nativeElement.scrollTop =
      53 + // header
      this.hour_segmented_height *
        6 * // cada hora
        (hour_decimal - 1); // -1 per posar-nos a l'inici de l'hora
  }

  private getEventColorObj(color: string): EventColor {
    return {
      primary: 'transparent',
      secondary: color,
    };
  }

  private getEventWorkingCategoryTitle(ecr: EmployeeCalendarRegisterWithRelationsSerialized) {
    if (!ecr.working_time_category_id) return 'Nuevo evento';
    return ecr.company_product_title ?? ecr.wtc_name ?? ecr.working_time_category?.name;
  }

  private getColorFromEcr(ecr: EmployeeCalendarRegisterWithRelationsSerialized) {
    if (!ecr.working_time_category_id) return '#cccccc';

    return ecr.company_product_color ?? ecr.wtc_color ?? ecr.working_time_category?.color ?? '#fff';
  }

  private async onDelete(event: CalendarEventCustomized) {
    if (this.event_selected_dialog_ref) {
      this._snackBar.open('Cierra el popup para eliminar este evento.', 'Ok', { duration: 3000 });
      return;
    }
    if (!this.checkPermissions(this.events, event)) {
      this._snackBar.open('No tienes permisos para eliminar este evento.', 'Ok', { duration: 3000 });
      return;
    }

    if (event.meta.ecr.working_time_category && !event.meta.ecr.working_time_category.is_editable_from_calendar) {
      this._snackBar.open('Este tipo de evento no se puede eliminar desde el calendario.', 'Ok', { duration: 3000 });
      return;
    }

    if (
      event.meta.ecr.user_id == null &&
      !confirm(
        '¿Estás seguro que quieres eliminar este evento? Es un evento compartido entre todos los empleados y desaparecerá para todo el mundo.'
      )
    ) {
      return;
    }

    if (event.meta.ecr.id != null && (typeof event.meta.ecr.id !== 'string' || !(event.meta.ecr.id as string).startsWith('tmp_'))) {
      let apply_updates_on: 'this' | 'next' | undefined = 'this';
      let notify: boolean | undefined = false;

      // preguntem si es vol propagar (solo el propietario / evento para todos puede elegir next)
      if ((event.meta.ecr as EmployeeCalendarRegisterWithRelationsSerialized).recurrence_freq != null) {
        const is_owner = event.meta.ecr.user_id == null || event.meta.ecr.user_id === this.me.id;
        if (is_owner) {
          apply_updates_on = await this.askChangesAppliableToThisOrNext();
          if (!apply_updates_on) return;
        } else {
          apply_updates_on = 'this';
        }
      }

      if ((event.meta.ecr.employee_calendar_register_user_shared_events ?? []).length > 0) {
        notify = await this.askNotifyOrNot();
        if (notify === undefined) {
          return;
        }
      }

      // la posem a eliminar
      this.deletable_ids.push({ id: event.meta.ecr.id as number, apply_updates_on: apply_updates_on, notify: notify });

      if (apply_updates_on === 'next') {
        this.events = this.events.filter(
          iEvent => !iEvent.meta?.ecr || iEvent.start < event.start || !this.isSameRecurrenceSeries(iEvent.meta.ecr, event.meta.ecr)
        );
      }
    }

    this.removeEventFromLocalCalendar(event);
    try {
      await this.save();
    } catch {
      this.events.push(event);
      this.refreshEvents();
    }
  }

  private removeEventFromLocalCalendar(event: CalendarEventCustomized): void {
    this.events = this.events.filter(iEvent => iEvent.id !== event.id);
    this.changesAvailableToSave = true;

    const avail_index = generateAvailKeyFromEcr(event.meta.ecr);
    if (avail_index != null && this.availability[avail_index]) {
      const diff = dayjs(event.end).diff(dayjs(event.start), 'hours', true);

      // eliminem del resum
      this.availability[avail_index].hours_assigned -= diff;

      this.setTotalHoursCreated();
    }

    this.refreshEvents();

    this.cleanAvailsThatAreNotGoingToBeUsed();
  }

  private initAbsencesAttribute(
    custom_user_absences_array: ('holiday' | 'vacation' | 'other' | null)[] | undefined = undefined
  ): ('holiday' | 'vacation' | 'other' | null)[] {
    let absences = custom_user_absences_array ?? this.absences; // agafa el de l'usuari si li passen sino
    absences = [null, null, null, null, null, null, null];
    return absences;
  }

  private createCalendarEventWithNullCategory(segment: WeekViewHourSegment): CalendarEventCustomized {
    const id: string = 'tmp_' + Math.random() * 100;
    const neutralColor = '#cccccc';
    return {
      id: id,
      color: this.getEventColorObj(neutralColor),
      title: 'Nuevo evento',
      subtitle: undefined as any,
      company_product_phase_name: undefined as any,
      start: segment.date,
      end: 60 / this.hour_segments > 1 ? addMinutes(segment.date, 60 / this.hour_segments) : addMinutes(segment.date, 60),
      resizable: { beforeStart: true, afterEnd: true },
      draggable: true,
      actions: [
        {
          label: '✕',
          onClick: ({ event }: { event: CalendarEventCustomized }): void => {
            void this.onDelete(event);
          },
        },
      ],
      height: null,
      meta: {
        tmpEvent: true,
        changed: true,
        loaded_user_id: this.user_selected.id,
        is_availability_view_mode: false,
        ecr: {
          id: id,
          from_date: segment.date,
          to_date: null as any,
          working_time_category_id: null as any,
          relatable_type: undefined,
          relatable_id: undefined,
          user_id: this.user_selected.id,
          custom_title: undefined,
          call_link: undefined,
          meet_mode: false,
          all_day: false,
          employee_calendar_register_user_shared_events: [],

          wtc_first_level_id: undefined as any,
          wtc_first_level_name: undefined as any,
          wtc_first_level_slug: undefined as any,
          wtc_first_level_color: undefined as any,
          wtc_id: undefined as any,
          wtc_name: undefined as any,
          wtc_color: undefined as any,
          is_working_time: true,

          company_product_id: null,
          company_product_title: null,
          company_product_color: null,
          company_product_phase_name: null,
          ecr_wtc_name: null,
        } as any,
        apply_updates_on: undefined,
        notify: undefined,
      },
    };
  }

  private cleanAvailsThatAreNotGoingToBeUsed() {
    for (const key of Object.keys(this.availability)) {
      if (this.availability[key] && this.availability[key].hours_assigned == 0 && this.availability[key].hours_to_do == 0) {
        delete this.availability[key];
      }
    }
  }

  private refreshEvents() {
    this.refresh.next();
    const eventContainers = this.elementRef.nativeElement.querySelectorAll('.cal-event-container');
    eventContainers.forEach((container: any) => {
      const childWithHeight = container.querySelector('[data-event-height]');
      if (childWithHeight) {
        const height = childWithHeight.getAttribute('data-event-height');
        container.style.height = height + 'px';
      }
    });
  }

  private setTotalHoursCreated() {
    let totalHoursCreated: number = 0;
    for (const key in this.availability) {
      if (this.availability[key] && this.availability[key].is_working_time) {
        totalHoursCreated += this.availability[key].hours_assigned;
      }
    }
    this.total_hours_created = totalHoursCreated;
  }

  private getMonday(d: Date) {
    return dayjs(d).startOf('week').toDate();
  }

  private getSunday(d: Date) {
    return dayjs(d).endOf('week').toDate();
  }

  private floatEvent(event: CalendarEventCustomized) {
    const cssClass: string = 'float';
    if (event.cssClass == undefined || event.cssClass == '') event.cssClass = cssClass;
    else if (!event.cssClass.includes('float')) event.cssClass += `,${cssClass}`;
  }

  private unfloatEvent(event: CalendarEventCustomized) {
    const cssClass: string = 'float';
    if (!(event.cssClass == undefined || event.cssClass == '')) {
      if (event.cssClass.includes(`,${cssClass}`)) event.cssClass = event.cssClass.replace(`,${cssClass}`, '');
      else if (event.cssClass.includes(cssClass)) event.cssClass = event.cssClass.replace(cssClass, '');
    }
  }

  private initFilterFormListener() {
    this.filterForm.valueChanges.pipe(debounceTime(500)).subscribe(data => {
      if (JSON.stringify(this.formPersistence) != JSON.stringify(data)) {
        this.router.navigate(['/employees', 'activity', 'calendar'], { queryParams: this.buildQueryParamsFromForm(data) });
      }
    });
  }

  private listenQueryParameters() {
    this.activatedRoute.queryParams.subscribe(params => {
      void this.handleQueryParametersChange(params);
    });
  }

  private async handleQueryParametersChange(params: Params): Promise<void> {
    if (this.reverting_filter_to_persistence) {
      return;
    }

    const ocupation_of_users = this.parseOcupationOfUsers(params['ocupation_of_users']);

    if (!this.initial_query_params_handled) {
      if (JSON.stringify(params) !== JSON.stringify(this.filterForm.value)) {
        const params_temp = this.utils.cloneObj(params);
        Object.keys(params_temp).forEach(param_key => {
          if (param_key === 'ocupation_of_users') return;
          if (params_temp[param_key] != null && params_temp[param_key] != '' && !isNaN(+params_temp[param_key]))
            params_temp[param_key] = +params_temp[param_key];
          else if (params_temp[param_key] != null && (params_temp[param_key] as string).indexOf('-') > -1)
            params_temp[param_key] = this.utils.stringToDate(params_temp[param_key]);
        });
        params_temp.ocupation_of_users = ocupation_of_users;
        this.filterForm.patchValue(params_temp, { emitEvent: false });
      }

      this.setOcupationOfUsersSelected(ocupation_of_users);
      this.viewDate.set(params.from != null ? this.utils.stringToDate(params.from) : this.viewDate());
      this.initial_query_params_handled = true;
      this.formPersistence = { ...this.filterForm.value, ocupation_of_users };
      return;
    }

    const next_from = params.from != null ? this.utils.stringToDate(params.from) : this.viewDate();
    const user_changed = params.user_id != null && this.formPersistence.user_id != params.user_id;
    const week_changed = params.from != null && !dayjs(next_from).isSame(dayjs(this.getPersistedFromDate()), 'week');

    if ((user_changed || week_changed) && !(await this.confirmLeaveUnsavedChanges())) {
      this.revertFilterToPersistence();
      return;
    }

    if (JSON.stringify(params) !== JSON.stringify(this.filterForm.value)) {
      const params_temp = this.utils.cloneObj(params);
      Object.keys(params_temp).forEach(param_key => {
        if (param_key === 'ocupation_of_users') return;
        if (params_temp[param_key] != null && params_temp[param_key] != '' && !isNaN(+params_temp[param_key]))
          params_temp[param_key] = +params_temp[param_key];
        else if (params_temp[param_key] != null && (params_temp[param_key] as string).indexOf('-') > -1)
          params_temp[param_key] = this.utils.stringToDate(params_temp[param_key]);
      });
      params_temp.ocupation_of_users = ocupation_of_users;
      this.filterForm.patchValue(params_temp, { emitEvent: false });
    }

    this.setOcupationOfUsersSelected(ocupation_of_users);
    this.viewDate.set(params.from != null ? this.utils.stringToDate(params.from) : this.viewDate());

    const previous_ocupation = this.parseOcupationOfUsers(this.formPersistence.ocupation_of_users);

    if (user_changed) {
      this.unsubscribeCurrentApiCalls();
      const user = this.userOptions.find(u => u.id == params.user_id) as User;
      if (user) this.selectUser(user.id);
    } else if (week_changed) {
      this.unsubscribeCurrentApiCalls();
      this.reloadWeeklyEvents();
    } else if (JSON.stringify(previous_ocupation) !== JSON.stringify(ocupation_of_users)) {
      void this.applyOcupationSelectionChange(previous_ocupation, ocupation_of_users);
    }

    this.formPersistence = { ...this.filterForm.value, ocupation_of_users };
  }

  private getPersistedFromDate(): Date {
    const from = this.formPersistence?.from;
    if (from instanceof Date) return from;
    if (from != null && from !== '') return this.utils.stringToDate(String(from));
    return this.filterForm.value['from'] as Date;
  }

  private changeWeek(from: Date) {
    const currentWeekStart: dayjs.Dayjs = dayjs(this.filterForm.value.from);
    if (!dayjs(from).isSame(currentWeekStart, 'week')) {
      this.viewDate.set(from);
      this.cdr.detectChanges();

      this.filterForm.patchValue({
        from: this.utils.dateToStringYYYYMMDD(dayjs(from).startOf('week').toDate()),
        to: this.utils.dateToStringYYYYMMDD(dayjs(from).endOf('week').toDate()),
      });
    }
  }

  private async reloadWeeklyEvents() {
    this.attendee_blocked_events_cache.clear();
    this.events = [];
    if (this.event_selected_dialog_ref != null && this.event_selected != null) this.events.push(this.event_selected);
    this.changesAvailableToSave = false;
    this.deletable_ids = [];
    this.fetchUserWeeklyAvailability();
    await this.fetchUserCalendarRegisters(true);
    this.fetchRemoteWorkingDays();
    await this.reloadWeeklyAvailabilityCompatibilityUsersData();
    this.refreshEvents();
  }

  private async selectUser(user_id: number) {
    this.user_selected = this.userOptions.find(u => u.id == user_id) as User;
    this.changesAvailableToSave = false;
    this.active_contract = null;
    this.calendarUrl = environment.laravel_url + '/api/v1/users/' + this.user_selected.id + '/calendar';
    await this.reloadWeeklyEvents();
  }

  private unsubscribeCurrentApiCalls() {
    for (const sub of this.api_subscriptions) {
      sub.unsubscribe();
    }
    this.api_subscriptions = [];
  }

  private presentEcrForViewedUser(
    ecr: EmployeeCalendarRegisterWithRelationsSerialized,
    viewed_user_id: number
  ): EmployeeCalendarRegisterWithRelationsSerialized {
    if (ecr.user_id == null || Number(ecr.user_id) === Number(viewed_user_id)) {
      return ecr;
    }

    const assignment = ecr.employee_calendar_register_user_shared_events?.find(
      shared_user => shared_user.user_id != null && Number(shared_user.user_id) === Number(viewed_user_id)
    );
    if (assignment == null) {
      return ecr;
    }

    const working_time_category = assignment.working_time_category ?? ecr.working_time_category;
    let company_product_id: number | undefined;
    let company_product_title: string | undefined;
    let company_product_color: string | undefined;
    let company_product_phase_name: string | undefined;

    if (assignment.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProduct) {
      company_product_id = assignment.relatable_id ?? assignment.relatable?.id ?? undefined;
      company_product_title = assignment.company_product_title ?? assignment.relatable?.title ?? undefined;
      company_product_color = assignment.company_product_color ?? assignment.relatable?.color ?? undefined;
    } else if (assignment.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProductPhase) {
      company_product_id = assignment.relatable?.company_product?.id ?? undefined;
      company_product_title = assignment.company_product_title ?? assignment.relatable?.company_product?.title ?? undefined;
      company_product_color = assignment.company_product_color ?? assignment.relatable?.company_product?.color ?? undefined;
      company_product_phase_name =
        assignment.company_product_phase_name ?? assignment.relatable?.company_product_phase_type?.name ?? undefined;
    }

    return {
      ...ecr,
      working_time_category_id: assignment.working_time_category_id ?? ecr.working_time_category_id,
      working_time_category,
      wtc_id: working_time_category?.id,
      wtc_name: working_time_category?.name,
      wtc_slug: working_time_category?.slug,
      wtc_color: working_time_category?.color,
      relatable_type: assignment.relatable_type ?? undefined,
      relatable_id: assignment.relatable_id ?? undefined,
      company_product_id,
      company_product_title,
      company_product_color,
      company_product_phase_name,
    };
  }

  private ecrToCalendarEventCustomized(
    ecr: EmployeeCalendarRegisterWithRelationsSerialized,
    is_quick_preview_for_row: AvailabilityCompatibilityUserRow | undefined = undefined
  ) {
    const original_ecr = ecr;
    ecr = this.presentEcrForViewedUser(original_ecr, is_quick_preview_for_row?.user.id ?? this.user_selected.id);
    const canManageEvent: boolean = !is_quick_preview_for_row && this.canManageEvent(original_ecr);
    const userSelectedIsMe: boolean = this.me.id == this.user_selected.id;
    const hasnt_started = dayjs(ecr.from_date).isAfter(dayjs().subtract(this.maxHoursOfMarginToManageEvents, 'hours'));
    const resizable: boolean = !is_quick_preview_for_row && canManageEvent && hasnt_started && userSelectedIsMe;
    const draggable: boolean = !is_quick_preview_for_row && canManageEvent && hasnt_started && userSelectedIsMe;
    const is_editable_from_calendar =
      (ecr.is_editable_from_calendar ?? true) && (ecr.working_time_category?.is_editable_from_calendar ?? true);
    const can_delete: boolean =
      !is_quick_preview_for_row && hasnt_started && userSelectedIsMe && is_editable_from_calendar && canManageEvent;

    // ECR Actions
    const actions: CalendarEventCustomizedAction[] = [];

    if (can_delete) {
      actions.push({
        label: '✕',
        onClick: ({ event }): void => {
          void this.onDelete(event);
        },
      });
    }

    if (ecr.call_link != null && ecr.call_link != '' && ecr.online_conference_type != null) {
      actions.push({
        label: '<img src="https://cdn-icons-png.flaticon.com/256/455/455691.png" alt="shared" width="12" style="margin-left: 4px;" />',
        onClick: (): void => {
          window.open(ecr.call_link, '_blank');
        },
      });
    }

    if (ecr.employee_calendar_register_user_shared_events != null && ecr.employee_calendar_register_user_shared_events.length) {
      actions.push({
        label: '<img src="assets/img/icons/group.png" alt="shared" width="14" style="margin-left: 4px;" />',
        onClick: (): void => {},
      });
    }

    if (ecr.trip_driver_user_id !== null && ecr.trip_driver_user_id !== undefined && ecr.trip_driver_user_id === this.me.id) {
      actions.push({
        label: '<img src="assets/img/icons/car.png" alt="car" width="14" style="margin-left: 4px;" />',
        onClick: (): void => {},
      });
    }

    const is_meeting_mode = this.is_meeting_mode_enabled && ecr.company_product_id !== this.meeting_mode_company_product;
    const is_restricted_view = !is_quick_preview_for_row && ecr.has_details_access === false;
    const is_private_event_view = is_meeting_mode || is_restricted_view;

    // Si el ECR está relacionado con un producto o fase, construir el evento con el color y título que corresponde
    let eventColor: string;
    let eventTitle: string;
    let eventSubtitle: string | null;
    let eventCompanyProductPhaseName: string | null;

    if (
      ecr.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProduct ||
      ecr.relatable_type === EmployeeCalendarRegisterRelatableType.CompanyProductPhase
    ) {
      // Priorizar el color del producto cuando está relacionado con producto o fase
      eventColor = !ecr.working_time_category_id
        ? '#cccccc'
        : (ecr.company_product_color ?? ecr.wtc_color ?? ecr.working_time_category?.color ?? '#fff');

      // Para productos y fases, usar el título del producto y mostrar la fase si existe
      eventTitle =
        is_quick_preview_for_row || is_private_event_view
          ? `Ocupado`
          : !ecr.working_time_category_id
            ? 'Nuevo evento'
            : (ecr.company_product_title ?? ecr.wtc_name ?? ecr.working_time_category?.name ?? '');
      eventSubtitle = is_private_event_view
        ? ''
        : is_quick_preview_for_row
          ? `para ${is_quick_preview_for_row.user.name}`
          : (ecr.custom_title ?? ecr.wtc_name ?? null);
      eventCompanyProductPhaseName = is_quick_preview_for_row || is_private_event_view ? null : (ecr.company_product_phase_name ?? null);
    } else {
      // Usar la lógica normal para otros casos
      eventColor = this.getColorFromEcr(ecr);
      eventTitle = is_quick_preview_for_row || is_private_event_view ? `Ocupado` : (this.getEventWorkingCategoryTitle(ecr) as string);
      eventSubtitle = is_private_event_view
        ? ''
        : is_quick_preview_for_row
          ? `para ${is_quick_preview_for_row.user.name}`
          : (ecr.custom_title ?? (eventTitle === ecr.ecr_wtc_name ? null : (ecr.wtc_name ?? null)));
      eventCompanyProductPhaseName = is_quick_preview_for_row || is_private_event_view ? null : (ecr.company_product_phase_name ?? null);
    }

    const title: string = eventTitle;
    const subtitle = eventSubtitle;
    const company_product_phase_name = eventCompanyProductPhaseName;
    const color = is_private_event_view
      ? { primary: 'transparent', secondary: this.privateEventCalendarColor, secondaryText: '#424242' }
      : is_quick_preview_for_row
        ? { primary: 'transparent', secondary: is_quick_preview_for_row.color ?? '#0c83e1' }
        : this.getEventColorObj(eventColor);
    const occupancy_css_class = is_quick_preview_for_row ? 'ocupado occupancy-preview' : undefined;

    return {
      id: is_quick_preview_for_row ? `ocupation-${is_quick_preview_for_row.user.id}-${ecr.id}` : ecr.id,
      allDay: ecr.all_day,
      title,
      subtitle,
      company_product_phase_name,
      color,
      cssClass: is_private_event_view ? 'ocupado' : occupancy_css_class,
      start: ecr.all_day ? dayjs(ecr.from_date).startOf('day').toDate() : new Date(ecr.from_date),
      end: ecr.all_day ? dayjs(ecr.to_date).subtract(12, 'hours').endOf('day').toDate() : new Date(ecr.to_date),
      resizable: { beforeStart: resizable, afterEnd: resizable },
      draggable: draggable,
      actions: actions,
      height: this.calculateHeightOfEvent(this.calculateDifferenceInMinutes(new Date(ecr.from_date), new Date(ecr.to_date)) ?? 5),
      meta: {
        tmpEvent: false,
        changed: false,
        ecr: original_ecr,
        loaded_user_id: original_ecr.user_id ?? null,
        is_availability_view_mode: is_quick_preview_for_row != undefined,
        availability_view_user_id: is_quick_preview_for_row?.user.id,
      },
    } as CalendarEventCustomized;
  }

  private canManageEvent(ecr: EmployeeCalendarRegisterWithRelationsSerialized) {
    return this.me.id == ecr.user_id || ecr.user_id == null;
    //  ||
    //         this.permissions.validateDepartment(['direction', 'rrhh']) ||
    //         (ecr.company_product_id != null && (
    //           this.permissions.isBisualProductionTeamInCompanyProductWithRole(ecr.company_product_id, 'tech-lead') ||
    //           this.permissions.isBisualProductionTeamInCompanyProductWithRole(ecr.company_product_id, 'product-manager')
    //         ));
  }

  private async onEventDialogClosed(
    data: EmployeeCalendarRegisterWithRelationsSerialized | undefined,
    old_start: Date,
    old_event: CalendarEventCustomized | undefined = undefined
  ) {
    if (this.event_selected != null) this.unfloatEvent(this.event_selected as CalendarEventCustomized);
    if (data) {
      // ha guardat els canvis
      const index = data.id != null ? this.events.findIndex(evt => evt.id == data.id) : -1;
      if (old_event && index >= 0) {
        const success: boolean = await this.handleEventUpdate(old_event, index, data);
        if (!success) return;
      } else {
        const event = this.ecrToCalendarEventCustomized(data, undefined);
        if ((data.employee_calendar_register_user_shared_events ?? []).length > 0) {
          const notify = await this.askNotifyOrNot();
          if (notify === undefined) {
            return;
          }
          event.meta.notify = notify;
        }
        this.handleEventCreationFinish(event, index);
      }
      this.restoreAvailabilityToPreDialogSnapshot();
      this.event_selected = undefined;
      this.event_selected_dialog_ref = undefined;
      await this.save();
      return;
    } else {
      this.restoreAvailabilityToPreDialogSnapshot();

      if (!dayjs(old_start).isSame(this.filterForm.value.from, 'week')) {
        // tornem a la setmana
        this.changeWeek(old_start);
      } else {
        // eliminem els canvis fets fins ara
        this.events.splice(this.events.indexOf(this.event_selected as CalendarEventCustomized), 1);
        if (old_event) this.events.push(old_event);
        this.refreshEvents();
      }
    }

    this.event_selected = undefined;
    this.event_selected_dialog_ref = undefined;

    return;
  }

  private restoreAvailabilityToPreDialogSnapshot(): void {
    const snapshot_ids = new Set(this.pre_dialog_availability_users_snapshot.map(r => r.user.id));
    const users_to_remove = this.availability_compatibility_users.filter(r => !snapshot_ids.has(r.user.id));
    for (const row of users_to_remove) {
      this.disableAvailabilityViewForUser(row.user);
    }
    this.pre_dialog_availability_users_snapshot = [];
  }

  private markEventAsChanged(event: CalendarEventCustomized) {
    event.meta.changed = true;
    if (event.cssClass == null || event.cssClass == '') event.cssClass = 'changed';
    else event.cssClass += ',changed';
  }

  private markEventAsUnchanged(event: CalendarEventCustomized) {
    event.meta.changed = false;
    if (event.cssClass == null || event.cssClass == '') event.cssClass = '';
    else if (event.cssClass.includes(',changed')) event.cssClass = event.cssClass.replace(',changed', '');
    else if (event.cssClass.includes('changed')) event.cssClass = event.cssClass.replace('changed', '');
  }

  private async fetchNonWorkingTimeCategoriesThatAppearInCalendar() {
    const promise = new Promise<void>(resolve => {
      const subscription = this.api.getWorkingTimeCategories({ is_working_time: 0 }).subscribe(
        data => {
          this.non_working_time_categories_that_appear_in_calendar = data;
        },
        error => {},
        () => {
          this.api_subscriptions.splice(
            this.api_subscriptions.findIndex(s => s === subscription),
            1
          );
          resolve();
        }
      );

      this.api_subscriptions.push(subscription);
    });
    await promise;
  }

  private async askChangesAppliableToThisOrNext() {
    const dialogRef = this.dialog.open(RadioSelectorDialogComponent, {
      width: '400px',
      data: {
        dialog_title: '¿Dónde quieres aplicar los cambios?',
        dialog_ok_button: 'Seleccionar',
        dialog_cancel_button: 'Cancelar',
        data: [
          { value: 'this', textToShow: 'Solo este evento' },
          { value: 'next', textToShow: 'Este y futuros eventos' },
        ],
      },
    });

    const apply_updates_on: 'this' | 'next' | undefined = await dialogRef.afterClosed().toPromise();

    return apply_updates_on;
  }

  private async askNotifyOrNot(): Promise<boolean | undefined> {
    const dialog_ref = this.dialog.open(RadioSelectorDialogComponent, {
      width: '400px',
      data: {
        dialog_title: '¿Quieres enviar notificaciones?',
        dialog_ok_button: 'Seleccionar',
        dialog_cancel_button: 'Cancelar',
        data: [
          { value: true, textToShow: 'Notificar a todo el mundo' },
          { value: false, textToShow: 'No notificar' },
        ],
      },
    });

    return firstValueFrom(dialog_ref.afterClosed());
  }

  private async handleEventUpdate(
    old_event: CalendarEventCustomized,
    old_event_idx: number,
    new_ecr: EmployeeCalendarRegisterWithRelationsSerialized
  ): Promise<boolean> {
    // Preservar propiedades esenciales del ECR original si están disponibles
    const originalEcr = old_event.meta.ecr;
    if (originalEcr) {
      // Solo preservar si el nuevo ECR no tiene estas propiedades
      if (!new_ecr.relatable_type && originalEcr.relatable_type) {
        new_ecr.relatable_type = originalEcr.relatable_type;
      }
      if (!new_ecr.relatable_id && originalEcr.relatable_id) {
        new_ecr.relatable_id = originalEcr.relatable_id;
      }

      const company_product_changed =
        new_ecr.relatable_id !== originalEcr.relatable_id || new_ecr.relatable_type !== originalEcr.relatable_type;

      if (!company_product_changed) {
        if (!new_ecr.company_product_color && originalEcr.company_product_color) {
          new_ecr.company_product_color = originalEcr.company_product_color;
        }
        if (!new_ecr.company_product_title && originalEcr.company_product_title) {
          new_ecr.company_product_title = originalEcr.company_product_title;
        }
        if (!new_ecr.company_product_phase_name && originalEcr.company_product_phase_name) {
          new_ecr.company_product_phase_name = originalEcr.company_product_phase_name;
        }
      }

      // Si el usuario cambió la categoría, usar las propiedades de la nueva categoría del diálogo
      if (new_ecr.working_time_category_id !== originalEcr.working_time_category_id) {
        // El diálogo ya envía las propiedades completas de la nueva categoría
        if (new_ecr.working_time_category) {
          new_ecr.wtc_name = new_ecr.working_time_category.name;
          new_ecr.wtc_color = new_ecr.working_time_category.color;
        }
      } else {
        // Solo preservar wtc_name si el usuario no cambió la categoría de trabajo
        if (!new_ecr.wtc_name && originalEcr.wtc_name) {
          new_ecr.wtc_name = originalEcr.wtc_name;
        }
        // Solo preservar wtc_color si el usuario no cambió la categoría de trabajo
        if (!new_ecr.wtc_color && originalEcr.wtc_color) {
          new_ecr.wtc_color = originalEcr.wtc_color;
        }
        // Solo preservar working_time_category si el usuario no cambió la categoría de trabajo
        if (!new_ecr.working_time_category && originalEcr.working_time_category) {
          new_ecr.working_time_category = originalEcr.working_time_category;
        }
      }
    }

    const event = this.ecrToCalendarEventCustomized(new_ecr, undefined);
    event.meta.loaded_user_id = old_event.meta.loaded_user_id ?? old_event.meta.ecr.user_id ?? null;

    // handle recurrence --> propagar
    if (
      old_event.meta.ecr.recurrence_freq != null &&
      !old_event.meta.apply_updates_on &&
      (typeof old_event.id !== 'string' || !old_event.id.startsWith('tmp_'))
    ) {
      const is_owner = old_event.meta.ecr.user_id == null || old_event.meta.ecr.user_id === this.me.id;
      if (!is_owner) {
        event.meta.apply_updates_on = 'this';
      } else if (this.recurrenceAttributesUnchanged(old_event.meta.ecr, new_ecr) && new_ecr.recurrence_freq != null) {
        let apply_updates_on: 'next' | 'this' | undefined = undefined;
        apply_updates_on = await this.askChangesAppliableToThisOrNext();
        if (!apply_updates_on) return false;
        else {
          event.meta.apply_updates_on = apply_updates_on;
        }
      } else {
        // si tenia recurrencia i s'ha eliminat, o bé s'ha tocat la recurrencia
        event.meta.apply_updates_on = 'next';
      }
    } else if (old_event.meta.apply_updates_on) {
      event.meta.apply_updates_on = old_event.meta.apply_updates_on; // propaguem
    }

    if (this.shouldAskToNotifySharedUsers(old_event, new_ecr)) {
      const notify = await this.askNotifyOrNot();
      if (notify === undefined) {
        return false;
      }
      event.meta.notify = notify;
    }

    // restem availabilities
    const old_avail_key = generateAvailKeyFromEcr(old_event.meta.ecr);
    if (old_avail_key in this.availability && this.availability[old_avail_key].is_working_time) {
      this.availability[old_avail_key].hours_assigned -= dayjs(old_event.end).diff(dayjs(old_event.start), 'minutes') / 60;
    }

    // aqui sumem altre cop les availabilities
    this.handleEventCreationFinish(event, old_event_idx);

    return true;
  }

  private shouldAskToNotifySharedUsers(
    old_event: CalendarEventCustomized,
    new_ecr: EmployeeCalendarRegisterWithRelationsSerialized
  ): boolean {
    const owner_id = old_event.meta.ecr.user_id;

    const is_owner = owner_id == null || owner_id === this.me.id;

    const event_has_shared_users =
      old_event.meta.ecr.employee_calendar_register_user_shared_events.length > 0 ||
      new_ecr.employee_calendar_register_user_shared_events.length > 0;

    return is_owner && event_has_shared_users;
  }

  private handleEventCreationFinish(event: CalendarEventCustomized, event_idx: number) {
    this.markEventAsChanged(event);
    if (event_idx >= 0) {
      this.events[event_idx] = event;
    } else {
      this.events.push(event);
    }
    this.changesAvailableToSave = true;

    this.avail_key_selected = generateAvailKeyFromEcr(event.meta.ecr);
    if (this.avail_key_selected in this.availability && this.availability[this.avail_key_selected].is_working_time) {
      this.availability[this.avail_key_selected].hours_assigned += dayjs(event.end).diff(dayjs(event.start), 'minutes') / 60;
    }

    this.refreshEvents();
  }

  private recurrenceAttributesUnchanged(
    old_ecr: EmployeeCalendarRegisterWithRelationsSerialized,
    new_ecr: EmployeeCalendarRegisterWithRelationsSerialized
  ) {
    return (
      old_ecr.recurrence_freq == new_ecr.recurrence_freq &&
      old_ecr.recurrence_interval == new_ecr.recurrence_interval &&
      old_ecr.recurrence_ending_count == new_ecr.recurrence_ending_count &&
      old_ecr.recurrence_ending_until == new_ecr.recurrence_ending_until &&
      old_ecr.recurrence_byday == new_ecr.recurrence_byday &&
      old_ecr.recurrence_bymonthday == new_ecr.recurrence_bymonthday &&
      old_ecr.recurrence_byyearday == new_ecr.recurrence_byyearday &&
      old_ecr.recurrence_byweekno == new_ecr.recurrence_byweekno &&
      old_ecr.recurrence_bymonth == new_ecr.recurrence_bymonth &&
      old_ecr.recurrence_bysetpos == new_ecr.recurrence_bysetpos
    );
  }

  private async reloadWeeklyAvailabilityCompatibilityUsersData() {
    this.ocupation_sync_promise = this.ocupation_sync_promise
      .then(() => this.reloadAllOcupationRowsForCurrentWeek())
      .catch(() => undefined);
    await this.ocupation_sync_promise;
  }

  private async reloadAllOcupationRowsForCurrentWeek(): Promise<void> {
    const target_ids = this.parseOcupationOfUsers(this.filterForm.value.ocupation_of_users)
      .filter(id => id !== this.me?.id)
      .map(id => +id);

    await this.syncOcupationSelectionDelta(
      this.availability_compatibility_users.map(row => +row.user.id),
      target_ids
    );

    if (this.availability_compatibility_users.length === 0) {
      this.availability_compatibility_events = [];
      this.mergeAvailabilityEventsIntoCalendar();
      return;
    }

    this.availability_compatibility_events = [];
    const start_week_monday = dayjs(this.filterForm.value['from']).startOf('isoWeek').format('YYYY-MM-DD');

    for (const row of this.availability_compatibility_users) {
      row.absences = (await this.fetchAbsences(row.user.id, [null, null, null, null, null, null, null]))!;
      const schedule = await this.getCachedAttendeeScheduleForWeek(row.user, start_week_monday);
      row.weekly_schedule = schedule?.weekly_schedule ?? null;
      row.weekly_schedule_exception = schedule?.weekly_schedule_exception ?? null;
      await this.loadOcupationEventsForRow(row, false);
    }

    this.mergeAvailabilityEventsIntoCalendar();
  }

  private async loadOcupationEventsForRow(
    row: AvailabilityCompatibilityUserRow,
    merge = true,
    options?: { allow_without_ocupation?: boolean }
  ): Promise<void> {
    const user_id = +row.user.id;
    if (!this.shouldLoadOcupationDataForUser(user_id, options?.allow_without_ocupation)) {
      return;
    }

    const existing = this.ocupation_events_load_promises.get(user_id);
    if (existing) {
      await existing;
      return;
    }

    const promise = this.doLoadOcupationEventsForRow(row, merge, options);
    this.ocupation_events_load_promises.set(user_id, promise);
    try {
      await promise;
    } finally {
      this.ocupation_events_load_promises.delete(user_id);
    }
  }

  private async doLoadOcupationEventsForRow(
    row: AvailabilityCompatibilityUserRow,
    merge = true,
    options?: { allow_without_ocupation?: boolean }
  ): Promise<void> {
    if (!this.shouldLoadOcupationDataForUser(row.user.id, options?.allow_without_ocupation)) {
      return;
    }

    const request_from = this.filterForm.value['from'];
    const request_to = this.filterForm.value['to'];

    const data = await this.getCachedAttendeeBlockedEventsForViewedWeek(row.user.id);

    if (
      !dayjs(request_from).isSame(this.filterForm.value['from'], 'day') ||
      !dayjs(request_to).isSame(this.filterForm.value['to'], 'day') ||
      !this.shouldLoadOcupationDataForUser(row.user.id, options?.allow_without_ocupation) ||
      !this.availability_compatibility_users.some(r => +r.user.id === +row.user.id)
    ) {
      return;
    }

    if (!data) {
      return;
    }

    this.availability_compatibility_events = this.availability_compatibility_events.filter(
      event => !this.isAvailabilityEventForUserId(event, row.user.id)
    );

    const own_event_ids = new Set(
      this.events
        .filter(event => event.meta?.is_availability_view_mode !== true)
        .map(event => event.meta?.ecr?.id)
        .filter((id): id is number => id != null)
    );
    const selected_ecr_id = this.event_selected?.meta?.ecr?.id;
    const row_events = data
      .filter(ecr => selected_ecr_id == null || selected_ecr_id != ecr.id)
      .filter(ecr => ecr.id == null || !own_event_ids.has(ecr.id))
      .map(ecr => this.ecrToCalendarEventCustomized(ecr, row));

    this.availability_compatibility_events = [...this.availability_compatibility_events, ...row_events];

    if (merge) {
      this.mergeAvailabilityEventsIntoCalendar();
    }
  }

  private mergeAvailabilityEventsIntoCalendar(): void {
    const selected = this.event_selected;
    const selected_id = selected?.id;
    const non_availability = this.events.filter(
      e => e.meta.is_availability_view_mode !== true && (selected_id == null || e.id !== selected_id)
    );
    const own_ecr_ids = new Set(
      [...non_availability, ...(selected ? [selected] : [])].map(event => event.meta?.ecr?.id).filter((id): id is number => id != null)
    );
    const availability_without_own_events = this.availability_compatibility_events.filter(event => {
      const ecr_id = event.meta?.ecr?.id;
      if (ecr_id != null && own_ecr_ids.has(ecr_id)) {
        return false;
      }
      return selected_id == null || event.id !== selected_id;
    });

    this.events = [...non_availability, ...(selected ? [selected] : []), ...availability_without_own_events];
    this.refreshEvents();
  }

  private worksOnDayAndTime(hours_ponderated: number, day_index: number): boolean {
    return this.worksOnDayAndTimeFromPeriods(hours_ponderated, this.getScheduleDayPeriodsForIndex(day_index));
  }

  private worksOnDayAndTimeFromPeriods(hours_ponderated: number, periods: WorkingContractWeeklyScheduleDayPeriod[]): boolean {
    if (periods.length === 0) {
      return false;
    }

    return periods.some(period => {
      const [sh, sm] = period.start_time.split(':').map(n => +n);
      const [eh, em] = period.end_time.split(':').map(n => +n);
      const start = sh + (sm ?? 0) / 60;
      const end = eh + (em ?? 0) / 60;
      return hours_ponderated >= start && hours_ponderated < end;
    });
  }

  private processFileImportCsv(file: File) {
    // Verificar que el archivo sea un ICS
    if (file.type !== 'text/calendar' && !file.name.endsWith('.ics')) {
      alert('Por favor, selecciona un archivo ICS válido.');
      return;
    }

    // Leer el contenido del archivo
    const reader = new FileReader();
    reader.onload = e => {
      const fileContent = e.target?.result;
      if (fileContent) this.onImportIcsWithContent(fileContent as string);
    };
    reader.readAsText(file);
  }

  private async onImportIcsWithContent(fileContent: string) {
    const jcalData = ICAL.parse(fileContent);
    const vcalendar = new ICAL.Component(jcalData);
    const event = vcalendar.getFirstSubcomponent('vevent');

    if (event) {
      let calendar_event_customized: CalendarEventCustomized = {} as any;

      const title: string = event.getFirstPropertyValue('summary') as string;
      const description: string = event.getFirstPropertyValue('description') as string;
      const allDay: boolean = event.getFirstPropertyValue('x-microsoft-cdo-alldayevent') === 'TRUE';
      const startTime: dayjs.Dayjs = dayjs(event.getFirstPropertyValue('dtstart') as any);
      const endTime: dayjs.Dayjs = dayjs(event.getFirstPropertyValue('dtend') as any);
      const link: string | undefined =
        (event.getFirstPropertyValue('x-microsoft-skypeteamsmeetingurl') as string) ??
        (event.getFirstPropertyValue('x-google-conference') as string) ??
        undefined;
      const uid: string | undefined = (event.getFirstPropertyValue('uid') as string) ?? undefined;
      const rrule:
        | {
            freq: string;
            interval: number | undefined;
            byday: string | undefined;
            until: Date | undefined;
            count: number | undefined;
            bymonthday: string | undefined;
            byyearday: string | undefined;
            byweekno: string | undefined;
            bymonth: string | undefined;
            bysetpos: string | undefined;
          }
        | undefined = event.getFirstPropertyValue('rrule') as any;

      // attendees
      const attendees = [];
      const organizer: string = event.getFirstPropertyValue('organizer') as string;
      if (organizer) attendees.push(organizer.split('mailto:')[1]);
      for (const attendee of event.getAllProperties('attendee')) {
        const email: string | undefined = attendee.getFirstValue()?.toString().split('mailto:')[1];
        if (email) {
          if (attendees.indexOf(email) === -1 && email !== this.user_selected.email && email !== 'developers.bisual@gmail.com') {
            attendees.push(email);
          }
        }
      }

      // after processing
      if (uid) {
        let ecr_already_created_request_data: EmployeeCalendarRegisterWithRelationsSerialized[] | undefined = await this.api
          .getEmployeeCalendarRegistersForUserSerialized(this.user_selected.id.toString(), undefined, undefined, {
            online_conference_id: uid,
          })
          .toPromise();
        if (ecr_already_created_request_data) {
          if (ecr_already_created_request_data.length > 0)
            calendar_event_customized = this.ecrToCalendarEventCustomized(ecr_already_created_request_data[0]);
          else {
            ecr_already_created_request_data = await this.api
              .getEmployeeCalendarRegistersForUserSerialized(this.user_selected.id.toString(), undefined, undefined, { id: uid })
              .toPromise();
            if (ecr_already_created_request_data && ecr_already_created_request_data.length > 0) {
              calendar_event_customized = this.ecrToCalendarEventCustomized(ecr_already_created_request_data[0]);
            }
          }
        }
      }

      if (!calendar_event_customized.start) {
        calendar_event_customized = {
          start: startTime.toDate(),
          end: endTime.toDate(),
          allDay: allDay,
          meta: {
            ecr: {
              user_id: this.user_selected.id,
              from_date: startTime.toDate(),
              to_date: endTime.toDate(),
              working_time_category_id: this.availability[this.avail_key_selected].working_time_category_id,
              all_day: allDay,
              custom_title: title,
              call_link: link,
              online_conference_type: 'external', // encara que sigui meet o teams posem external perque no el gestionem nosaltres
              online_conference_id: uid,
              employee_calendar_register_user_shared_events: [] as EmployeeCalendarRegisterUserSharedEvent[],
              description: description,
            } as EmployeeCalendarRegisterWithRelationsSerialized | unknown,
          },
        } as CalendarEventCustomized;
      } else {
        // s'han de posar els atributs que s'hagin de sobrescriure
      }

      if (!dayjs(calendar_event_customized.start).isSame(dayjs(this.viewDate()), 'week')) {
        this.changeWeek(calendar_event_customized.start);
        await this.utils.sleep(1000);
      }

      // rrule
      if (rrule && rrule.freq) {
        calendar_event_customized.meta.ecr.recurrence_freq = rrule.freq.toLowerCase() as any;
        calendar_event_customized.meta.ecr.recurrence_interval = rrule.interval ?? 1;
        if (rrule.count) calendar_event_customized.meta.ecr.recurrence_ending_count = rrule.count;
        if (rrule.until) calendar_event_customized.meta.ecr.recurrence_ending_until = dayjs(rrule.until).toDate();
        if (rrule.byday)
          calendar_event_customized.meta.ecr.recurrence_byday = rrule.byday.split(',').map(day => {
            if (day === 'MO') return 1;
            else if (day === 'TU') return 2;
            else if (day === 'WE') return 3;
            else if (day === 'TH') return 4;
            else if (day === 'FR') return 5;
            else if (day === 'SA') return 6;
            else if (day === 'SU') return 7;
            else throw `Day ${day} not recognized.`;
          });
        if (rrule.bymonthday) calendar_event_customized.meta.ecr.recurrence_bymonthday = rrule.bymonthday.split(',').map(n => +n);
        if (rrule.byyearday) calendar_event_customized.meta.ecr.recurrence_byyearday = rrule.byyearday.split(',').map(n => +n);
        if (rrule.byweekno) calendar_event_customized.meta.ecr.recurrence_byweekno = rrule.byweekno.split(',').map(n => +n);
        if (rrule.bymonth) calendar_event_customized.meta.ecr.recurrence_bymonth = rrule.bymonth.split(',').map(n => +n);
        if (rrule.bysetpos) calendar_event_customized.meta.ecr.recurrence_bysetpos = rrule.bysetpos.split(',').map(n => +n);
      } else {
        // posem tot a null
        calendar_event_customized.meta.ecr.recurrence_freq =
          calendar_event_customized.meta.ecr.recurrence_interval =
          calendar_event_customized.meta.ecr.recurrence_ending_count =
          calendar_event_customized.meta.ecr.recurrence_ending_until =
          calendar_event_customized.meta.ecr.recurrence_byday =
          calendar_event_customized.meta.ecr.recurrence_bymonthday =
          calendar_event_customized.meta.ecr.recurrence_byyearday =
          calendar_event_customized.meta.ecr.recurrence_byweekno =
          calendar_event_customized.meta.ecr.recurrence_bymonth =
          calendar_event_customized.meta.ecr.recurrence_bysetpos =
            undefined;
      }

      for (const email of attendees) {
        const users: ArrayResponse<User> | undefined = await this.api.getUsers({ search: email }).toPromise();
        if (users != undefined && users.data.length > 0) {
          calendar_event_customized.meta.ecr.employee_calendar_register_user_shared_events.push({
            user_id: users.data[0].id,
            user: users.data[0],
          } as EmployeeCalendarRegisterUserSharedEvent);
        } else
          calendar_event_customized.meta.ecr.employee_calendar_register_user_shared_events.push({
            anonymous_user_email: email,
          } as EmployeeCalendarRegisterUserSharedEvent);
      }

      this.eventClicked(calendar_event_customized);
    }
  }

  private openConfigureScheduleDialog(data: ConfigureWorkingScheduleDialogData): void {
    this.refreshViewedWeekSchedules((): void => {
      if (!this.viewedWeekSchedulesLoaded()) {
        return;
      }
      const dialogRef = this.dialog.open(ConfigureWorkingScheduleDialogComponent, {
        width: '1200px',
        maxWidth: '95vw',
        data,
        autoFocus: false,
      });
      dialogRef.afterClosed().subscribe((saved): void => {
        if (saved === true) {
          const week_monday = dayjs(this.filterForm.value['from']).startOf('isoWeek').format('YYYY-MM-DD');
          this.invalidateAttendeeScheduleCache(this.me.id, week_monday);
          this.refreshViewedWeekSchedules((): void => {});
          this.reloadWeeklyEvents();
        }
      });
    });
  }

  private async fetchAttendeeCalendarEventsForUser(user_id: number): Promise<EmployeeCalendarRegisterWithRelationsSerialized[]> {
    return this.getCachedAttendeeBlockedEventsForViewedWeek(user_id);
  }

  private async getCachedAttendeeBlockedEventsForViewedWeek(user_id: number): Promise<EmployeeCalendarRegisterWithRelationsSerialized[]> {
    const cache_key = this.attendeeViewedWeekCacheKey(user_id);
    const cached = this.attendee_blocked_events_cache.get(cache_key);
    if (cached !== undefined) {
      return cached;
    }

    const pending_load_promise = this.attendee_blocked_events_pending_loads.get(cache_key);
    if (pending_load_promise) {
      return pending_load_promise;
    }

    const promise = this.loadAttendeeBlockedEvents(user_id, cache_key);
    this.attendee_blocked_events_pending_loads.set(cache_key, promise);
    try {
      return await promise;
    } finally {
      this.attendee_blocked_events_pending_loads.delete(cache_key);
    }
  }

  private async loadAttendeeBlockedEvents(user_id: number, cache_key: string): Promise<EmployeeCalendarRegisterWithRelationsSerialized[]> {
    const events = await firstValueFrom(
      this.api
        .getEmployeeCalendarRegistersForUserSerialized(user_id.toString(), this.filterForm.value['from'], this.filterForm.value['to'], {
          is_blocked_for_meetings: true,
        })
        .pipe(catchError(() => of([] as EmployeeCalendarRegisterWithRelationsSerialized[])))
    );

    this.attendee_blocked_events_cache.set(cache_key, events);

    return events;
  }

  private intervalsOverlap(start_a: Date, end_a: Date, start_b: Date, end_b: Date): boolean {
    return start_a.getTime() < end_b.getTime() && start_b.getTime() < end_a.getTime();
  }

  private getBlockedMeetingEcrsForViewedWeek(): EmployeeCalendarRegisterWithRelationsSerialized[] {
    const week_start = dayjs(this.filterForm.value['from']).startOf('isoWeek');
    const week_end = dayjs(this.filterForm.value['to']).endOf('isoWeek');

    const ecrs = this.events
      .filter(event => event.meta?.is_availability_view_mode !== true)
      .map(event => event.meta?.ecr)
      .filter((ecr): ecr is EmployeeCalendarRegisterWithRelationsSerialized => ecr != null)
      .filter(ecr => ecr.is_blocked_for_meetings === true)
      .filter(ecr => ecr.is_working_time === true)
      .filter(ecr => {
        const event_start = dayjs(ecr.from_date);
        const event_end = dayjs(ecr.to_date);
        return event_start.isBefore(week_end) && event_end.isAfter(week_start);
      });

    return Array.from(new Map(ecrs.map(ecr => [ecr.id, ecr])).values());
  }

  private getWeekAbsenceDays(): DayOfWeekEnum[] {
    if (!Array.isArray(this.absences) || this.absences.length === 0) {
      return [];
    }

    const days_with_absence: DayOfWeekEnum[] = [];
    this.absences.forEach((absence, index) => {
      if (absence == null) {
        return;
      }
      const iso_day = index === 0 ? 7 : index;
      days_with_absence.push(fromDayOfWeekIso(iso_day));
    });

    return days_with_absence;
  }
}

export interface Avail {
  name: {
    title: string;
    company: string | undefined;
    company_id: number | undefined;
    company_product_category: string | undefined;
    company_product_category_id: number | undefined;
    company_product_phase_name: string | undefined;
    company_product_id: number | undefined;
  };
  color: string;
  working_time_category_id: number;
  working_time_category_slug: string | undefined;
  is_working_time: boolean;
  relatable_id: number | undefined;
  relatable_type: EmployeeCalendarRegisterRelatableType | undefined;
  hours_to_do: number;
  hours_assigned: number;
}

function floorToNearest(amount: number, precision: number) {
  return Math.floor(amount / precision) * precision;
}

function ceilToNearest(amount: number, precision: number) {
  return Math.ceil(amount / precision) * precision;
}

export type AttendeeCalendarConflict =
  | { user: User; reason: AttendeeCalendarConflictReason.OutOfHours }
  | { user: User; reason: AttendeeCalendarConflictReason.TimeOff }
  | { user: User; reason: AttendeeCalendarConflictReason.BlockedEvent; conflicting_event: EmployeeCalendarRegisterWithRelationsSerialized };

enum AttendeeCalendarConflictReason {
  OutOfHours = 'out_of_hours',
  TimeOff = 'time_off',
  BlockedEvent = 'blocked_event',
}

export type CalendarEventCustomizedAction = Omit<EventAction, 'onClick'> & {
  onClick(args: { event: CalendarEventCustomized; sourceEvent: MouseEvent | KeyboardEvent }): void;
};

export interface CalendarEventCustomized extends CalendarEvent {
  height: number | null;
  // totes les propietats de CalendarEvent
  subtitle: string | undefined;
  company_product_phase_name: string | undefined;
  actions?: CalendarEventCustomizedAction[];
  meta: {
    tmpEvent?: boolean;
    changed: boolean;
    ecr: EmployeeCalendarRegisterWithRelationsSerialized;
    /** Snapshot of owner when the event was loaded; used to send user_id only on null ↔ value transitions. */
    loaded_user_id?: number | null;
    is_availability_view_mode: boolean;
    availability_view_user_id?: number;
    apply_updates_on: 'this' | 'next' | undefined;
    notify: boolean | undefined;
    hide_tooltip?: boolean;
  };
}

export type CalendarEventDetailDialogCloseResult =
  | EmployeeCalendarRegisterWithRelationsSerialized
  | { calendar_dialog_action: 'delete' }
  | undefined;

export function generateAvailKeyFromEcr(ecr: EmployeeCalendarRegisterWithRelationsSerialized) {
  return `wtc_${ecr.working_time_category_id}`;
}

export function isAllowedEmployeeCalendarRegisterUserIdPayloadChange(
  from_user_id: number | null | undefined,
  to_user_id: number | null | undefined,
  requesting_user_id: number
): boolean {
  const from = from_user_id == null ? null : +from_user_id;
  const to = to_user_id == null ? null : +to_user_id;

  if (from !== null && to === null) {
    return true;
  }

  if (from === null && to !== null) {
    return to === +requesting_user_id;
  }

  return false;
}

interface CachedAttendeeSchedule {
  weekly_schedule: WorkingContractWeeklySchedule | null;
  weekly_schedule_exception: WorkingContractWeeklyScheduleException | null;
}

interface CachedAttendeeAbsences {
  holidays: Holiday[];
  employee_calendar_registers: EmployeeCalendarRegister[];
}

interface AvailabilityCompatibilityUserRow {
  user: User;
  color: string;
  absences: ('holiday' | 'vacation' | 'other' | null)[];
  weekly_schedule: WorkingContractWeeklySchedule | null;
  weekly_schedule_exception: WorkingContractWeeklyScheduleException | null;
}
